import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  OnDestroy,
  inject,
  ViewChild,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import {
  forkJoin,
  Observable,
  of,
  Subject,
  Subscription,
  firstValueFrom,
} from 'rxjs';
import {
  map,
  switchMap,
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  tap,
} from 'rxjs/operators';
import { OrderControllerService } from '@app/api/order-service/api/orderController.service';
import { OrderTableDTO } from '@app/api/order-service/model/orderTableDTO';
import { ProvinceService } from '@app/feature/center-business/channel/service/province.service';
import { VietnamAddressService } from '@app/feature/center-business/channel/warehouse/services/vietnam-address.service';
import { formatCurrencyVND } from '@app/shared/utils/date-formatter.util';
import { OrderCreateLayoutComponent } from '@shared/components/order-create-layout/order-create-layout.component';
import {
  ProductSelectionDialogComponent,
  ProductItem,
} from '../../order/internal/components/product-selection-dialog/product-selection-dialog.component';
import {
  ProductService,
  ProductSearchParams,
} from '../../order/internal/services/internal.service';
import {
  isPrePackedPackageFulfillment,
  PACKAGE_FULFILLMENT_MODE,
  PRODUCT_SERVICE_CONFIG,
} from '../../order/internal/internal.config';
import {
  O2O_CREATE_CONFIG,
  OrderProduct,
  PackageSkuChild,
  O2O_TAKING_DELIVERY_WAY,
} from '../services/o2o.config';
import { O2OOrderService, SalesStoreOption } from '../services/o2o-order.service';
import { CouponService } from '../../marketing/coupon/services/coupon.service';
import { withPaginatedFormFields } from '@shared/utils/paginated-field.helpers';
import {
  PaginatedDropdownLoadParams,
  PaginatedOptionsPage,
} from '@shared/utils/paginated-dropdown.util';
import type { FormField } from '@shared/components/add-form/add-form.component';
import { PromotionCheckoutService } from '@app/api/promotion-service/api/promotionCheckout.service';
import { OrderTableDTO as PromotionOrderTableDTO } from '@app/api/promotion-service/model/orderTableDTO';
import { OrderBuyerInfoDTO as PromotionOrderBuyerInfoDTO } from '@app/api/promotion-service/model/orderBuyerInfoDTO';
import { OrderItemDTO as PromotionOrderItemDTO } from '@app/api/promotion-service/model/orderItemDTO';
import {
  extractPromotionCouponCodes,
  formatPromotionDateTime,
} from '../services/o2o-promotion-checkout.util';
import { OrderDTO } from '@app/api/stock-service/model/orderDTO';
import { OrderReceiverInfoDTO } from '@app/api/stock-service/model/orderReceiverInfoDTO';
import { OrderItemDTO as StockOrderItemDTO } from '@app/api/stock-service/model/orderItemDTO';
import { DistanceWarehouseResVO } from '@app/api/stock-service/model/distanceWarehouseResVO';
import { GoodsSkuControllerService } from '@app/api/goods-service/api/goodsSkuController.service';
import { PackageConversionDetectRequestDTO } from '@app/api/goods-service/model/packageConversionDetectRequestDTO';
import { PackageConversionDetectResponseVO } from '@app/api/goods-service/model/packageConversionDetectResponseVO';
import { PackageConversionDetectedPackageVO } from '@app/api/goods-service/model/packageConversionDetectedPackageVO';
import { RpcOrderResVO } from '@app/api/goods-service/model/rpcOrderResVO';
import { CanComponentDeactivate } from '@app/core/auth/can-component-deactivate.interface';
import {
  DataTableComponent,
  TableColumn,
} from '@shared/components/data-table/data-table.component';

@Component({
  selector: 'app-o2o-create',
  standalone: true,
  imports: [
    OrderCreateLayoutComponent,
    TranslateModule,
    ProductSelectionDialogComponent,
    ToastModule,
    DialogModule,
    ButtonModule,
    DataTableComponent,
  ],
  templateUrl: './o2o-create.component.html',
  styleUrl: './o2o-create.component.css',
  changeDetection: ChangeDetectionStrategy.Default,
})
export class O2OCreateComponent
  implements OnInit, OnDestroy, CanComponentDeactivate
{
  private readonly router = inject(Router);
  private readonly orderService = inject(OrderControllerService);
  private readonly productService = inject(ProductService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly translateService = inject(TranslateService);
  private readonly messageService = inject(MessageService);
  private readonly provinceService = inject(ProvinceService);
  private readonly vietnamAddressService = inject(VietnamAddressService);
  private readonly o2oOrderService = inject(O2OOrderService);
  private readonly couponService = inject(CouponService);
  private readonly promotionCheckout = inject(PromotionCheckoutService);
  private readonly goodsSkuController = inject(GoodsSkuControllerService);

  // Delivery method constants
  private readonly DELIVERY_METHOD_PICKUP = 'pickup';
  private readonly DELIVERY_METHOD_DELIVERY = 'delivery';
  /** Expose for template (class.o2o-pickup-mode when pickup) */
  readonly O2O_TAKING_DELIVERY_WAY = O2O_TAKING_DELIVERY_WAY;

  loading = false;
  error: string | null = null;
  private skipCanDeactivate = false;
  // Nearest warehouse data from API response (includes distance and duration)
  private nearestWarehouses: DistanceWarehouseResVO[] = [];
  /** Sales-store options for nearest warehouses, sorted by distanceMeters ASC. */
  private nearestSalesStoreOptions: Array<{ label: string; value: number }> = [];
  private hasAvailableSalesStores = false;
  // Loading state for stores dropdown
  storesLoading = false;
  private previousTakingDeliveryWay: string | undefined;
  private reloadStoresSubscription: Subscription | null = null;
  private readonly takingDeliveryWaySubject = new Subject<string>();
  private couponSearchSub: Subscription | null = null;
  private promotionAvailableSub: Subscription | null = null;
  selectedOfferIds: number[] = [];
  selectedOfferLabels: string[] = [];
  showPromotionSelectionDialog = false;
  promotionDialogLoading = false;
  promotionDialogOffers: Array<{
    offerId: number;
    offerCode: string;
    offerName: string;
    description: string;
    validity: string;
    priority: number | null;
  }> = [];
  promotionDialogSelection: Array<{
    offerId: number;
    offerCode: string;
    offerName: string;
    description: string;
    validity: string;
    priority: number | null;
  }> = [];
  promotionDialogColumns: TableColumn[] = [
    { prop: 'offerCode', label: 'o2o.create.promotion_dialog.offer_code' },
    { prop: 'offerName', label: 'o2o.create.promotion_dialog.offer_name' },
    { prop: 'description', label: 'o2o.create.promotion_dialog.description' },
    { prop: 'validity', label: 'o2o.create.promotion_dialog.validity' },
    { prop: 'priority', label: 'o2o.create.promotion_dialog.priority' },
  ];
  selectedPromotionOfferChips: Array<{ offerId: number; offerName: string }> =
    [];
  private readonly nearestWarehouseSubject = new Subject<void>();
  private nearestWarehouseSubscription: Subscription | null = null;
  private readonly packageConversionChanges = new Subject<void>();
  private packageConversionSubscription: Subscription | null = null;
  private packageConversionRequestId = 0;

  // Product selection dialog state
  showProductSelectionDialog = false;
  private _productDialogProducts: ProductItem[] = [];
  productDialogLoading = false;
  productDialogTotalRecords = 0;
  productDialogCurrentPage = 1;
  productDialogPageSize = PRODUCT_SERVICE_CONFIG.defaultPageSize;
  // Store current search parameters to maintain filters when changing pages
  private productDialogSearchParams: Record<string, string> = {};

  // Getter with logging to track what data is passed to dialog
  get productDialogProducts(): ProductItem[] {
    return this._productDialogProducts;
  }

  set productDialogProducts(value: ProductItem[]) {
    this._productDialogProducts = value || [];
  }

  // Track already selected products for disabling in dialog
  private  alreadySelectedSkuCodes = new Set<string>();

  // Coupon-related properties
  selectedCoupon: any = null; // maintained for backward compatibility, not used in multi-coupon flow
  couponDiscountAmount = 0;
  couponInputError: string | null = null;
  private lastValidatedCode: string | null = null;
  private isRemovingCoupon = false; // Flag to prevent re-validation during removal
  private updateSummaryTimeout: any = null;
  private readonly lastCalculatedOrderAmount: number | null = null;
  private readonly isShowingToast = false;
  private lastToastTime = 0;
  private couponValidationTimeout: any = null;
  // Multi-coupon state
  appliedCoupons: any[] = [];
  couponInputValue: string = '';
  couponSuggestions: { [key: string]: any[] } = {};

  // Configuration from config file — deep-cloned so mutations don't affect the shared singleton
  headerActions = O2O_CREATE_CONFIG.headerActions.map((a) => ({ ...a }));
  tableSections: typeof O2O_CREATE_CONFIG.tableSections = O2O_CREATE_CONFIG.tableSections.map(
    (section) => ({
      ...section,
      columns: section.columns ? [...section.columns] : [],
      data: [],
      selection: [],
      actions: section.actions ? section.actions.map((act) => ({ ...act })) : [],
    }),
  ) as any;
  summarySection: typeof O2O_CREATE_CONFIG.summarySection = {
    ...O2O_CREATE_CONFIG.summarySection,
    items: O2O_CREATE_CONFIG.summarySection.items.map((item) => ({ ...item })),
  };
  formFields: FormField[] = [];
  selectedStore: SalesStoreOption | undefined;
  provinceOptions = signal<Array<{ label: string; value: number }>>([]);
  wardOptions = signal<Array<{ label: string; value: number }>>([]);

  // Form data for initialData input
  formData: any = {};

  // ViewChild to access the order-create-layout component (compat proxy exposes form API)
  @ViewChild('addForm') addFormComponent!: OrderCreateLayoutComponent;

  ngOnInit(): void {
    this.formFields = this.buildFormFields();

    // Reset component state to ensure clean start
    this.resetComponentState();
    this.setDefaultValues();
    this.setMaxDateForDateFields();
    this.updateSummary();

    // Load data in parallel for better performance
    this.loadInitialData();

    // Ensure selection array is properly initialized
    if (!this.tableSections[0].selection) {
      this.tableSections[0].selection = [];
    }
    // Initialize table actions (disable delete button when no items)
    this.updateTableActions([]);
    this.updateCouponFieldState();
    // Initialize coupon chips and remove callback
    // Chips are rendered inside p-autocomplete (multiple mode). No external chip rendering.

    // Initialize address and province validation based on default takingDeliveryWay
    this.updateAddressAndProvinceValidation(O2O_TAKING_DELIVERY_WAY.pickup);

    // Initialize payment fields visibility based on default payment status
    this.updatePaymentFieldsVisibility(0); // Unpaid by default

    // Set up debounced subscription for takingDeliveryWay changes
    this.setupTakingDeliveryWaySubscription();

    // Set up debounced subscription for nearest warehouse API calls
    this.setupNearestWarehouseSubscription();
    this.setupPackageConversionSubscription();

    // Set up reactive subscriptions for province/ward/product changes
    this.setupReactiveSubscriptions();

    // Set up coupon input subscription after form is initialized
    setTimeout(() => {
      // Auto-apply coupon on typing has been removed.
      // Coupon flow is now SEARCH (completeMethod) -> SELECT (onSelect) only.
    }, 100);
  }

  /**
   * Set up reactive subscriptions for province/ward/product changes
   */
  private setupReactiveSubscriptions(): void {
    // Wait for form to be initialized
    setTimeout(() => {
      if (!this.addFormComponent || !this.addFormComponent.form) {
        return;
      }

      const provinceControl = this.addFormComponent.form.get('provinceId');
      const wardControl = this.addFormComponent.form.get('communeWardId');

      // Subscribe to province changes
      if (provinceControl && provinceControl.valueChanges) {
        provinceControl.valueChanges
          .pipe(debounceTime(300), distinctUntilChanged())
          .subscribe(() => {
            this.tryCallNearestWarehouse();
          });
      }

      // Subscribe to ward changes
      if (wardControl && wardControl.valueChanges) {
        wardControl.valueChanges
          .pipe(debounceTime(300), distinctUntilChanged())
          .subscribe(() => {
            this.tryCallNearestWarehouse();
          });
      }
    }, 200);
  }

  /**
   * Set up debounced subscription for takingDeliveryWay changes
   * This prevents duplicate API calls when the value changes
   */
  private setupTakingDeliveryWaySubscription(): void {
    this.reloadStoresSubscription = this.takingDeliveryWaySubject
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(() => {
        this.rebuildNearestSalesStoreOptionsAndRefresh(false);
      });
  }

  ngOnDestroy(): void {
    // Clean up timeouts to prevent memory leaks
    if (this.updateSummaryTimeout) {
      clearTimeout(this.updateSummaryTimeout);
    }
    if (this.couponValidationTimeout) {
      clearTimeout(this.couponValidationTimeout);
    }
    // Clean up subscriptions
    if (this.reloadStoresSubscription) {
      this.reloadStoresSubscription.unsubscribe();
    }
    if (this.couponSearchSub) {
      this.couponSearchSub.unsubscribe();
      this.couponSearchSub = null;
    }
    if (this.promotionAvailableSub) {
      this.promotionAvailableSub.unsubscribe();
      this.promotionAvailableSub = null;
    }
    if (this.nearestWarehouseSubscription) {
      this.nearestWarehouseSubscription.unsubscribe();
      this.nearestWarehouseSubscription = null;
    }
    if (this.packageConversionSubscription) {
      this.packageConversionSubscription.unsubscribe();
      this.packageConversionSubscription = null;
    }
    this.takingDeliveryWaySubject.complete();
    this.nearestWarehouseSubject.complete();
    this.packageConversionChanges.complete();
  }

  private buildFormFields(): FormField[] {
    return withPaginatedFormFields(O2O_CREATE_CONFIG.formFields.map((f) => ({ ...f })), {
      salesStore: {
        loadPage: (params) => this.loadSalesStorePage(params),
        mapCurrentToOption: (value) => {
          const selected = this.nearestSalesStoreOptions.find(
            (option) => option.value === Number(value),
          );
          if (selected) {
            return selected;
          }
          const store = this.o2oOrderService.getSalesStoreById(Number(value));
          return store
            ? { label: store.label, value: store.value }
            : { label: String(value ?? ''), value: Number(value) };
        },
      },
      provinceId: {
        loadPage: (params) => this.provinceService.getProvinceOptionsPage(params),
      },
      communeWardId: {
        loadPage: (params) =>
          this.vietnamAddressService.getWardOptionsPage(
            this.addFormComponent?.form?.get('provinceId')?.value ??
              this.formData.provinceId,
            params,
          ),
      },
    });
  }

  /** Load province labels for nearest-warehouse API (dropdown uses paginated loader). */
  private loadInitialData(): void {
    this.provinceService.getProvinceOptions().subscribe({
      next: (provinces) => {
        this.provinceOptions.set(provinces);
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error loading initial data:', error);
        this.error = this.translateService.instant(
          'common.messages.failed_to_load_data',
        );
        this.cdr.detectChanges();
      },
    });
  }

  /**
   * Reload sales stores based on takingDeliveryWay
   * pickup -> selfPickupFlag = true
   * delivery -> selfPickupFlag = undefined (get all stores)
   * Uses debounced subject to prevent duplicate API calls
   */
  private reloadSalesStores(takingDeliveryWay: string): void {
    // Only emit if value actually changed
    if (this.previousTakingDeliveryWay !== takingDeliveryWay) {
      this.previousTakingDeliveryWay = takingDeliveryWay;
      this.takingDeliveryWaySubject.next(takingDeliveryWay);
    }
  }

  private getProvinceName(provinceId: number): string {
    const province = this.provinceOptions().find((p) => p.value === provinceId);
    return province ? province.label : '';
  }

  private getWardName(wardId: number): string {
    const ward = this.wardOptions().find((w) => w.value === wardId);
    return ward ? ward.label : '';
  }

  /**
   * Translate duration text from English to Vietnamese using i18n keys
   * Example: "2 min 56 sec" -> "2 minutes 56 seconds"
   */
  private translateDurationToVietnamese(duration: string): string {
    if (!duration) return '';

    // Map English time units to i18n keys
    const translations: Record<string, string> = {
      min: 'common.time.minute',
      sec: 'common.time.second',
      hour: 'common.time.hour',
      hours: 'common.time.hour',
      day: 'common.time.day',
      days: 'common.time.day',
    };

    let translated = duration;
    for (const [english, i18nKey] of Object.entries(translations)) {
      // Use word boundary to match whole words only
      const regex = new RegExp(`\\b${english}\\b`, 'gi');
      translated = translated.replace(
        regex,
        this.translateService.instant(i18nKey),
      );
    }

    return translated;
  }

  /**
   * Load commune/ward options based on selected province
   */
  private loadCommuneWardOptions(provinceId: number | null | undefined): void {
    if (!provinceId) {
      this.wardOptions.set([]);

      // Clear selected commune/ward if it exists
      if (
        this.formData.communeWardId &&
        this.addFormComponent?.form
      ) {
        this.addFormComponent.form.patchValue({ communeWardId: null });
      }

      this.addFormComponent?.reloadPaginatedField('communeWardId');
      this.cdr.detectChanges();
      return;
    }

    // Load wards for the selected province
    this.vietnamAddressService.loadWardsByProvince(provinceId).subscribe({
      next: (wards) => {
        const communeWardOptions = wards.map((ward) => ({
          label: ward.full_name,
          value: ward.id,
        }));

        this.wardOptions.set(communeWardOptions);

        // Clear selected commune/ward if it's no longer in the list
        if (this.formData.communeWardId) {
          const wardStillExists = wards.some(
            (ward) => ward.id === this.formData.communeWardId,
          );
          if (!wardStillExists) {
            this.formData.communeWardId = null;
            this.addFormComponent?.form?.patchValue({ communeWardId: null });
          }
        }

        this.addFormComponent?.reloadPaginatedField('communeWardId');
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error loading commune/ward options:', error);
        this.wardOptions.set([]);
        this.addFormComponent?.reloadPaginatedField('communeWardId');

        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.messages.error'),
          detail: this.translateService.instant(
            'common.messages.failed_to_load_data',
          ),
        });

        this.cdr.detectChanges();
      },
    });
  }

  /**
   * Try to call nearest warehouse API
   * Only calls if all conditions are met: products exist AND province exists AND ward exists
   * Otherwise clears nearest warehouse and updates store list
   */
  private tryCallNearestWarehouse(): void {
    // Check if products exist
    const allItems = this.tableSections[0].data || [];
    if (allItems.length === 0) {
      // No products, clear nearest warehouse and update stores
      this.nearestWarehouses = [];
      this.nearestSalesStoreOptions = [];
      this.hasAvailableSalesStores = false;
      this.refreshSalesStoreDropdown();
      return;
    }

    // Check if province exists (ward is optional)
    const provinceId = this.formData.provinceId;
    if (!provinceId) {
      // Province not selected, clear nearest warehouse and update stores
      this.nearestWarehouses = [];
      this.nearestSalesStoreOptions = [];
      this.hasAvailableSalesStores = false;
      this.refreshSalesStoreDropdown();
      return;
    }

    // Emit to subject for debounced API call
    // The actual API call will be made in setupNearestWarehouseSubscription
    // with current form values at that time
    // Ward is optional - if it exists, it will be included in the payload
    this.nearestWarehouseSubject.next();
  }

  /**
   * Setup debounced subscription for nearest warehouse API calls
   */
  private setupNearestWarehouseSubscription(): void {
    this.nearestWarehouseSubscription = this.nearestWarehouseSubject
      .pipe(
        debounceTime(300), // Wait 300ms after the last change
        switchMap(() => {
          // Get current form values
          const allItems = this.tableSections[0].data || [];
          const provinceId = this.formData.provinceId;
          const communeWardId = this.formData.communeWardId;

          // Double-check conditions before calling API
          // Products and province are required, ward is optional
          if (allItems.length === 0 || !provinceId) {
            return of([]);
          }

          // Set loading state before API call
          this.storesLoading = true;
          this.updateSalesStoreFieldState();

          // Build order items
          const items: StockOrderItemDTO[] = allItems.map((item: any) => ({
            skuCode: item.skuCode,
            quantity: item.quantity || 1,
          }));

          // Build receiver info - use labels instead of codes
          // Province is required, district (ward) is optional
          const receiverInfo: OrderReceiverInfoDTO = {};
          if (provinceId) {
            receiverInfo.province = this.getProvinceName(provinceId);
          }
          // Include district only if ward is selected
          if (communeWardId) {
            receiverInfo.district = this.getWardName(communeWardId);
          }

          // Build order DTO
          const orderDTO: OrderDTO = {
            items: items,
            receiverInfo: receiverInfo,
          };

          // Call API
          return this.o2oOrderService.getNearestFulfillWarehouse(orderDTO);
        }),
        catchError((error) => {
          console.error('Error calling nearest warehouse API:', error);
          this.storesLoading = false;
          this.nearestWarehouses = [];
          this.nearestSalesStoreOptions = [];
          this.hasAvailableSalesStores = false;
          this.refreshSalesStoreDropdown();
          return of([]);
        }),
      )
      .subscribe({
        next: (warehouses) => {
          this.nearestWarehouses = warehouses || [];
          this.rebuildNearestSalesStoreOptionsAndRefresh(true);
          if (
            this.requiresNearestWarehouseCheck() &&
            this.nearestWarehouses.length === 0
          ) {
            this.showNoFulfillWarehouseError();
          }
        },
      });
  }

  /** Products + province are set — nearest warehouse API must return fulfillable stock */
  /**
   * Detect complete package conversions after the effective order-item list changes.
   * A cart version prevents stale responses from changing a newer O2O order draft.
   */
  private setupPackageConversionSubscription(): void {
    this.packageConversionSubscription = this.packageConversionChanges
      .pipe(
        debounceTime(300),
        map(() => this.buildPackageConversionRequest()),
        filter(
          (
            context,
          ): context is {
            request: PackageConversionDetectRequestDTO;
          } => context !== null,
        ),
        switchMap(({ request }) => {
          const requestId = ++this.packageConversionRequestId;
          this.setPackageConversionTableLoading(true);

          return this.goodsSkuController.detectPackageConversions(
            request,
            'body',
            false,
            { httpHeaderAccept: 'application/json' as any },
          ).pipe(
            map((response) => ({ response, requestId })),
            // Detection is optional enrichment. Retain the current order on 404/network failures.
            catchError(() => of({ response: null, requestId })),
            finalize(() => {
              if (requestId === this.packageConversionRequestId) {
                this.setPackageConversionTableLoading(false);
              }
            }),
          );
        }),
      )
      .subscribe((conversion) => {
        if (!conversion?.response) {
          return;
        }
        this.applyPackageConversionResult(conversion.response);
      });
  }

  private requestPackageConversionDetection(): void {
    this.packageConversionChanges.next();
  }

  private setPackageConversionTableLoading(loading: boolean): void {
    this.tableSections = this.tableSections.map((section, index) =>
      index === 0 ? { ...section, loading } : section,
    ) as typeof this.tableSections;
    this.cdr.markForCheck();
  }

  private buildPackageConversionRequest(): {
    request: PackageConversionDetectRequestDTO;
  } | null {
    const items = (this.tableSections[0].data || [])
      .filter(
        (item: any) =>
          !this.isPackageOrderItem(item) &&
          Number.isInteger(Number(item.skuId)) &&
          !!item.skuCode &&
          Number.isInteger(Number(item.quantity)) &&
          Number(item.quantity) > 0,
      )
      .map((item: any) => ({
        skuId: Number(item.skuId),
        skuCode: String(item.skuCode),
        quantity: Number(item.quantity),
      }));

    if (!items.length) {
      return null;
    }

    return {
      request: { items },
    };
  }

  private applyPackageConversionResult(
    response: PackageConversionDetectResponseVO,
  ): void {
    const detectedPackages = (response.detectedPackages || []).filter(
      (detectedPackage) => this.isApplicableDetectedPackage(detectedPackage),
    );
    if (!detectedPackages.length) {
      return;
    }

    const existingItems = this.tableSections[0].data || [];
    const existingDirectPackages = existingItems.filter(
      (item: any) => this.isPackageOrderItem(item),
    );
    const remainingSingles = this.buildRemainingSingleItems(
      existingItems,
      detectedPackages,
      response,
    );
    const packageItems = detectedPackages.map((detectedPackage, index) =>
      this.createPackageOrderItem(detectedPackage, index),
    );
    const mergedPackageItems = this.mergePackageOrderItems(
      existingDirectPackages,
      packageItems,
    );
    const items = [...mergedPackageItems, ...remainingSingles];
    const itemIds = new Set(items.map((item: any) => item.id));
    this.tableSections = this.tableSections.map((section, index) =>
      index === 0
        ? {
            ...section,
            data: items,
            selection: (section.selection || []).filter((item: any) =>
              itemIds.has(item.id),
            ),
          }
        : section,
    ) as typeof this.tableSections;
    this.updateAlreadySelectedSkuCodes();
    this.updateTableActions(this.tableSections[0].selection || []);
    this.loadPackageChildren(mergedPackageItems);
    this.updateCouponFieldState();
    this.refreshPromotionPreviewAndSummary();
    this.tryCallNearestWarehouse();
    this.cdr.detectChanges();
  }

  private createPackageOrderItem(
    detectedPackage: PackageConversionDetectedPackageVO,
    index: number,
  ): any {
    const packageSku = detectedPackage.packageSku!;
    const quantity = Number(detectedPackage.packageQuantity);
    const packageUnitPrice = this.getDetectedPackageUnitPrice(detectedPackage);
    const timestamp = Date.now() + index;

    return this.withUpdatedOrderItemTotals({
      id: timestamp,
      orderItemCode: `ITEM-${timestamp}`,
      skuCode: packageSku.skuCode,
      skuId: packageSku.skuId,
      skuName: packageSku.skuName || '',
      spuName: packageSku.spuName || '',
      spuCode: packageSku.spuCode || '',
      quantity,
      thirdPartyPrice: packageUnitPrice,
      internalPrice: packageUnitPrice,
      costPrice: Number(packageSku.costPrice) || 0,
      giftFlag: false,
      spuId: packageSku.spuId,
      setFlag: 1,
      isPackage: true,
      fulfillmentMode: PACKAGE_FULFILLMENT_MODE.virtualBundle,
      shippingFee: 0,
      type: 'product',
    });
  }

  private mergePackageOrderItems(
    existingPackageItems: any[],
    detectedPackageItems: any[],
  ): any[] {
    const packageItemsBySkuCode = new Map<string, any>();

    [...existingPackageItems, ...detectedPackageItems].forEach((item) => {
      const skuKey = this.toSkuKey(item.skuCode);
      const existingItem = packageItemsBySkuCode.get(skuKey);
      packageItemsBySkuCode.set(
        skuKey,
        existingItem
          ? this.withUpdatedOrderItemTotals({
              ...existingItem,
              quantity: Number(existingItem.quantity) + Number(item.quantity),
            })
          : item,
      );
    });

    return Array.from(packageItemsBySkuCode.values());
  }

  private isApplicableDetectedPackage(
    detectedPackage: PackageConversionDetectedPackageVO,
  ): boolean {
    return (
      !!detectedPackage.packageSku?.skuCode &&
      !isPrePackedPackageFulfillment(
        detectedPackage.packageSku.fulfillmentMode,
      ) &&
      Number.isInteger(Number(detectedPackage.packageQuantity)) &&
      Number(detectedPackage.packageQuantity) > 0 &&
      Number.isFinite(this.getDetectedPackageUnitPrice(detectedPackage))
    );
  }

  private getDetectedPackageUnitPrice(
    detectedPackage: PackageConversionDetectedPackageVO,
  ): number {
    return Number(
      detectedPackage.packageUnitPrice ?? detectedPackage.packageSku?.salesPrice,
    );
  }

  private buildRemainingSingleItems(
    existingItems: any[],
    detectedPackages: PackageConversionDetectedPackageVO[],
    response: PackageConversionDetectResponseVO,
  ): any[] {
    const consumedQuantityBySku = new Map<string, number>();
    detectedPackages.forEach((detectedPackage) => {
      (detectedPackage.componentMappings || []).forEach((component) => {
        const skuKey = this.toSkuKey(component.skuCode);
        const consumedQuantity = Number(component.consumedQuantity) || 0;
        if (skuKey && consumedQuantity > 0) {
          consumedQuantityBySku.set(
            skuKey,
            (consumedQuantityBySku.get(skuKey) || 0) + consumedQuantity,
          );
        }
      });
    });

    const responseRemainingQuantityBySku = new Map(
      (response.remainingSingleItems || []).map((item) => [
        this.toSkuKey(item.skuCode),
        Number(item.quantity) || 0,
      ]),
    );
    const useConsumedQuantities = consumedQuantityBySku.size > 0;

    return existingItems
      .filter((item) => !this.isPackageOrderItem(item))
      .map((item) => {
        const skuKey = this.toSkuKey(item.skuCode);
        const remainingQuantity = useConsumedQuantities
          ? Math.max(
              0,
              (Number(item.quantity) || 0) -
                (consumedQuantityBySku.get(skuKey) || 0),
            )
          : responseRemainingQuantityBySku.get(skuKey) ?? 0;

        return remainingQuantity > 0
          ? this.withUpdatedOrderItemTotals({ ...item, quantity: remainingQuantity })
          : null;
      })
      .filter((item): item is Record<string, unknown> => item !== null);
  }

  private isPackageOrderItem(item: { setFlag?: unknown; isPackage?: unknown }): boolean {
    return Number(item.setFlag) === 1 || item.isPackage === true || Number(item.isPackage) === 1;
  }

  private isPrePackedPackage(
    item: {
      setFlag?: unknown;
      isPackage?: unknown;
      fulfillmentMode?: unknown;
    },
  ): boolean {
    return (
      this.isPackageOrderItem(item) &&
      isPrePackedPackageFulfillment(item.fulfillmentMode)
    );
  }

  private withUpdatedOrderItemTotals(item: any): any {
    const quantity = Number(item.quantity) || 1;
    return {
      ...item,
      quantity,
      costTotal: (Number(item.costPrice) || 0) * quantity,
      thirdPartyTotal: (Number(item.thirdPartyPrice) || 0) * quantity,
      internalTotal: (Number(item.internalPrice) || 0) * quantity,
      shippingFee: Number(item.shippingFee) || 0,
    };
  }

  private requiresNearestWarehouseCheck(): boolean {
    const allItems = this.tableSections[0]?.data || [];
    return allItems.length > 0 && !!this.formData.provinceId;
  }

  private showNoFulfillWarehouseError(): void {
    this.messageService.add({
      severity: 'error',
      summary: this.translateService.instant('common.messages.error'),
      detail: this.translateService.instant(
        'o2o.create.form.validation.no_fulfill_warehouse',
      ),
      life: 5000,
    });
  }

  private validateNearestWarehouseForSave(): boolean {
    if (!this.requiresNearestWarehouseCheck()) {
      return true;
    }
    if (this.storesLoading) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'o2o.create.form.validation.nearest_warehouse_loading',
        ),
        life: 5000,
      });
      return false;
    }
    if (this.nearestWarehouses.length === 0 || !this.hasAvailableSalesStores) {
      this.showNoFulfillWarehouseError();
      return false;
    }
    return true;
  }

  private getSelfPickupFlag(): boolean | undefined {
    const takingDeliveryWay =
      this.addFormComponent?.form?.get('takingDeliveryWay')?.value ??
      this.formData.takingDeliveryWay ??
      O2O_TAKING_DELIVERY_WAY.pickup;
    return takingDeliveryWay === O2O_TAKING_DELIVERY_WAY.pickup
      ? true
      : undefined;
  }

  private loadSalesStorePage(
    params: PaginatedDropdownLoadParams,
  ): Observable<PaginatedOptionsPage<number>> {
    if (this.nearestWarehouses.length === 0) {
      if (params.page === 1 && !params.searchText.trim()) {
        this.hasAvailableSalesStores = false;
      }
      return of({ options: [], pageContentLength: 0 });
    }

    const search = params.searchText.trim().toLowerCase();
    const filtered = search
      ? this.nearestSalesStoreOptions.filter((option) =>
          option.label.toLowerCase().includes(search),
        )
      : this.nearestSalesStoreOptions;

    const start = Math.max(0, (params.page - 1) * params.size);
    const pageOptions = filtered.slice(start, start + params.size);

    if (params.page === 1 && !search) {
      this.hasAvailableSalesStores = filtered.length > 0;
    }

    return of({
      options: pageOptions,
      pageContentLength: pageOptions.length,
      totalElements: filtered.length,
      hasNextPage: start + params.size < filtered.length,
    });
  }

  private rebuildNearestSalesStoreOptionsAndRefresh(
    forceSelectClosest: boolean,
  ): void {
    if (this.nearestWarehouses.length === 0) {
      this.nearestSalesStoreOptions = [];
      this.hasAvailableSalesStores = false;
      this.storesLoading = false;
      this.refreshSalesStoreDropdown();
      return;
    }

    this.storesLoading = true;
    this.updateSalesStoreFieldState();

    const warehouseMap = new Map<number, DistanceWarehouseResVO>();
    this.nearestWarehouses.forEach((warehouse) => {
      if (warehouse.id != null) {
        warehouseMap.set(Number(warehouse.id), warehouse);
      }
    });

    this.o2oOrderService
      .getSalesStoresPage({
        page: 1,
        size: 500,
        searchText: '',
        status: true,
        selfPickupFlag: this.getSelfPickupFlag(),
      })
      .pipe(
        tap((page) => {
          type StoreOptionWithDistance = {
            label: string;
            value: number;
            distanceMeters: number;
          };

          const matched: StoreOptionWithDistance[] = [];
          for (const option of page.options) {
            const store = this.o2oOrderService.getSalesStoreById(
              Number(option.value),
            );
            if (
              store?.physicalWarehouseId == null ||
              !warehouseMap.has(Number(store.physicalWarehouseId))
            ) {
              continue;
            }

            const warehouse = warehouseMap.get(
              Number(store.physicalWarehouseId),
            );
            if (!warehouse) {
              continue;
            }

            const distance = warehouse.distanceMetersInText || '';
            matched.push({
              label: distance ? `${store.label} (${distance})` : store.label,
              value: store.value,
              distanceMeters:
                warehouse.distanceMeters ?? Number.POSITIVE_INFINITY,
            });
          }

          matched.sort((a, b) => a.distanceMeters - b.distanceMeters);

          this.nearestSalesStoreOptions = matched.map(({ label, value }) => ({
            label,
            value,
          }));
          this.hasAvailableSalesStores = this.nearestSalesStoreOptions.length > 0;
        }),
        finalize(() => {
          this.storesLoading = false;
          this.selectDefaultClosestSalesStore(forceSelectClosest);
          this.refreshSalesStoreDropdown();
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        error: (error) => {
          console.error('Error rebuilding nearest sales store options:', error);
          this.nearestSalesStoreOptions = [];
          this.hasAvailableSalesStores = false;
        },
      });
  }

  private selectDefaultClosestSalesStore(forceSelectClosest = false): void {
    if (this.nearestSalesStoreOptions.length === 0) {
      this.selectedStore = undefined;
      this.addFormComponent?.form?.patchValue({ salesStore: null });
      return;
    }

    if (
      !forceSelectClosest &&
      this.selectedStore &&
      this.isStoreAvailableForNearestWarehouses(this.selectedStore) &&
      this.nearestSalesStoreOptions.some(
        (option) => option.value === this.selectedStore?.value,
      )
    ) {
      return;
    }

    const closest = this.nearestSalesStoreOptions[0];
    this.selectedStore =
      this.o2oOrderService.getSalesStoreById(closest.value) ?? undefined;
    this.addFormComponent?.form?.patchValue({ salesStore: closest.value });
  }

  private refreshSalesStoreDropdown(): void {
    this.clearInvalidSelectedStore();
    this.updateSalesStoreFieldState();
    this.addFormComponent?.reloadPaginatedField('salesStore');
    this.cdr.detectChanges();
  }

  private updateSalesStoreFieldState(): void {
    const salesStoreField = this.formFields.find(
      (field) => field.controlName === 'salesStore',
    );
    if (!salesStoreField) {
      return;
    }

    salesStoreField.loading = this.storesLoading;
    salesStoreField.emptyMessage =
      !this.storesLoading && !this.hasAvailableSalesStores
        ? this.translateService.instant('o2o.create.form.messages.no_available_store')
        : undefined;
    this.formFields = [...this.formFields];
  }

  private clearInvalidSelectedStore(): void {
    if (!this.selectedStore) {
      return;
    }

    const storeStillValid = this.isStoreAvailableForNearestWarehouses(
      this.selectedStore,
    );
    if (!storeStillValid) {
      this.selectedStore = undefined;
      this.addFormComponent?.form?.patchValue({ salesStore: null });
    }
  }

  private isStoreAvailableForNearestWarehouses(
    store: SalesStoreOption,
  ): boolean {
    if (this.nearestWarehouses.length === 0) {
      return false;
    }
    if (store.physicalWarehouseId === undefined || store.physicalWarehouseId === null) {
      return false;
    }

    return this.nearestWarehouses.some(
      (warehouse) => Number(warehouse.id) === Number(store.physicalWarehouseId),
    );
  }

  /**
   * Promotion checkout flow:
   * (a) POST /v1/promotions/checkout/available — when cart + store + address ready.
   * (b) POST /v1/coupons/validate — customer coupon codes.
   * (c) POST /v1/promotions/checkout/preview — only when coupon codes or selected offers exist.
   * Commit after order create only when preview was applicable.
   */
  private hasPromotionCheckoutContext(
    formValue?: Record<string, unknown>,
  ): boolean {
    const v =
      formValue ?? this.addFormComponent?.form?.value ?? this.formData ?? {};
    const items = this.tableSections[0]?.data || [];
    if (items.length === 0) {
      return false;
    }
    if (!v['salesStore']) {
      return false;
    }
    if (!v['provinceId']) {
      return false;
    }
    if (!v['communeWardId']) {
      return false;
    }
    const takingDeliveryWay =
      v['takingDeliveryWay'] ?? O2O_TAKING_DELIVERY_WAY.pickup;
    const isDelivery = takingDeliveryWay === O2O_TAKING_DELIVERY_WAY.delivery;
    if (isDelivery) {
      const address = ((v['shippingAddress'] as string) || '').trim();
      if (!address) {
        return false;
      }
    }
    return true;
  }

  private buildOrderBuyerInfoDTO(
    formValue: Record<string, unknown>,
  ): PromotionOrderTableDTO['orderBuyerInfoDTO'] {
    return {
      buyerNick: (formValue['buyerNickname'] as string) || '',
      receiverName: (formValue['recipient'] as string) || '',
      mobile: (formValue['recipientPhone'] as string) || '',
      phone: (formValue['recipientPhone'] as string) || '',
      buyerRemarks: (formValue['buyerRemarks'] as string) || '',
      address: (formValue['shippingAddress'] as string) || '',
      provinceCode: String(formValue['provinceId'] ?? ''),
      province: this.getProvinceName(Number(formValue['provinceId'])) || '',
      cityCode: '',
      city: (formValue['city'] as string) || '',
      districtCode: '',
      district: (formValue['district'] as string) || '',
      town: (formValue['town'] as string) || '',
      wardCode: String(formValue['communeWardId'] ?? ''),
      ward: this.getWardName(Number(formValue['communeWardId'])) || '',
    };
  }

  private getPromotionChannelCode(): string {
    const formVal = this.addFormComponent?.form?.value || this.formData || {};
    return formVal?.salesChannel || 'website';
  }

  private getPromotionCouponCodes(): string[] {
    return extractPromotionCouponCodes(this.appliedCoupons);
  }

  /** True when user applied coupon(s) or selected eligible promotion offer(s). */
  private hasPromotionCheckoutSelection(
    couponCodes?: string[],
    selectedOfferIds?: number[],
  ): boolean {
    const codes = couponCodes ?? this.getPromotionCouponCodes();
    const offers = selectedOfferIds ?? this.selectedOfferIds;
    return codes.length > 0 || offers.length > 0;
  }

  private async fetchPromotionPreviewTotalDiscount(
    formValue?: Record<string, unknown>,
  ): Promise<number> {
    const couponCodes = this.getPromotionCouponCodes();
    const selectedOfferIds = [...this.selectedOfferIds];
    if (!this.hasPromotionCheckoutSelection(couponCodes, selectedOfferIds)) {
      return 0;
    }

    const dto = this.buildPromotionOrderTableDTO(formValue, { forPreview: true });
    if (!dto) {
      return 0;
    }

    dto.orderPaymentInfoDTO.couponCodes = couponCodes;
    dto.orderPaymentInfoDTO.selectedOfferIds = selectedOfferIds;

    try {
      const previewResult = await firstValueFrom(
        this.promotionCheckout.preview(dto, 'body', false, {
          httpHeaderAccept: 'application/json' as any,
        }),
      );
      return Math.round(previewResult?.totalDiscount ?? 0);
    } catch (error) {
      console.error('Preview promotions failed:', error);
      return 0;
    }
  }

  private buildPromotionOrderTableDTO(
    formValue?: Record<string, unknown>,
    options?: { forPreview?: boolean },
  ): PromotionOrderTableDTO | null {
    const actualFormValue =
      formValue ?? this.addFormComponent?.form?.value ?? this.formData ?? {};
    if (!this.hasPromotionCheckoutContext(actualFormValue)) {
      return null;
    }
    const allItems = this.tableSections[0]?.data || [];

    const goodsTotalAmount = allItems.reduce(
      (sum, item) => sum + (item.internalTotal || 0),
      0,
    );
    const costTotalAmount = allItems.reduce(
      (sum, item) => sum + (item.costPrice || 0),
      0,
    );
    const sellingTotalAmount = allItems.reduce(
      (sum, item) => sum + (item.thirdPartyTotal || 0),
      0,
    );
    const shippingAmount = this.formData.shippingAmount || 0;
    const paymentAmount = options?.forPreview
      ? sellingTotalAmount + shippingAmount
      : sellingTotalAmount + shippingAmount - this.couponDiscountAmount;

    const selectedStoreData =
      this.selectedStore ||
      this.o2oOrderService.getSalesStoreById(Number(actualFormValue['salesStore']));
    const storeName = selectedStoreData?.label || '';
    const cleanStoreName = storeName.replace(
      /\s*\(\d+\.?\d*\s*km\)\s*$/i,
      '',
    );
    const storeCode = selectedStoreData?.spuCode || '';
    const isPaid = Number(actualFormValue['paymentStatus']) === 1;

    const orderItemDTOList: PromotionOrderItemDTO[] = allItems.map(
      (item: OrderProduct, index: number) => {
        const row = item as OrderProduct & {
          skuId?: number;
          giftFlag?: boolean;
          orderItemCode?: string;
          setFlag?: number;
        };
        return {
        orderItemCode:
          row.orderItemCode || `ITEM-${Date.now()}-${index + 1}`,
        skuId: row.skuId!,
        skuCode: item.skuCode,
        quantity: item.quantity,
        outerSaleAmount: item.thirdPartyTotal,
        outerSingleSaleAmount: item.thirdPartyPrice,
        innerSaleAmount: item.internalTotal,
        innerSingleSaleAmount: item.internalPrice,
        outBalancePrice: 0,
        innerBalancePrice: 0,
        setMealFlag: row.setFlag ?? 0,
        giftFlag: row.giftFlag ? 1 : 0,
        mainOrderItemCode: '',
        transPrice: 0,
        systemPickFlag: true,
        activityId: 1,
      };
      },
    );

    return {
      orderBuyerInfoDTO: this.buildOrderBuyerInfoDTO(actualFormValue),
      orderBaseInfoDTO: {
        channel: (actualFormValue['salesChannel'] as string) || 'website',
        storeId: Number(actualFormValue['salesStore']) || 0,
        storeCode,
        storeName: cleanStoreName,
        outerOrderNo: (actualFormValue['thirdPartyOrderNumber'] as string) || '',
        businessType: 'O2O',
        orderTime: formatPromotionDateTime(
          actualFormValue['thirdPartyOrderTime'],
        ),
        payStatus: Number(actualFormValue['paymentStatus']) || 0,
        orderRemarks: (actualFormValue['merchantRemarks'] as string) || '',
        actionType: 'CREATE',
        copyOrigin: '',
        takingDeliveryWay:
          (actualFormValue['takingDeliveryWay'] as string) ||
          O2O_TAKING_DELIVERY_WAY.pickup,
        priceGroupCode: storeCode,
      },
      orderPaymentInfoDTO: {
        payType: isPaid
          ? ((actualFormValue['paymentMethod'] as string) || '')
          : '',
        payAmount: paymentAmount,
        goodsTotalAmount,
        costTotalAmount,
        payTime: formatPromotionDateTime(
          isPaid
            ? actualFormValue['paymentTime']
            : actualFormValue['thirdPartyOrderTime'],
        ),
        couponCodes: this.getPromotionCouponCodes(),
        selectedOfferIds: [...this.selectedOfferIds],
      },
      orderItemDTOList,
    };
  }

  private validateCouponCode(couponCode: string): Observable<any> {
    const code = (couponCode || '').trim();
    if (!code) {
      return of(null);
    }
    return this.validateIssuedCoupon({ couponCode: code });
  }

  private validateIssuedCoupon(coupon: {
    couponCode: string;
  }): Observable<{ couponCode: string } | null> {
    const code = (coupon?.couponCode || '').trim();
    if (!code) {
      return of(null);
    }
    return this.couponService
      .validateIssuedCouponCode(code, this.getPromotionChannelCode())
      .pipe(
        map((res) => (res?.valid === true ? { couponCode: code } : null)),
        catchError((err) => {
          console.error('Error validating issued coupon:', err);
          return of(null);
        }),
      );
  }

  private searchCoupons(couponCode: string): Observable<any[]> {
    return this.couponService.searchIssuedCoupons(couponCode);
  }

  private extractAvailableOffers(res: unknown): Array<{
    offerId?: number;
    displayName?: string;
    code?: string;
    shortDescription?: string;
    startDate?: string;
    endDate?: string;
    priority?: number;
    status?: string;
    lifecycleStatus?: string;
    requiresEnteredPromotionCode?: boolean;
  }> {
    let payload: Record<string, unknown> = {};
    if (typeof res === 'string') {
      try {
        payload = JSON.parse(res) as Record<string, unknown>;
      } catch {
        return [];
      }
    } else if (res && typeof res === 'object') {
      payload = res as Record<string, unknown>;
    }
    const offers = payload['offers'] ?? payload['data'];
    return Array.isArray(offers) ? offers : [];
  }

  private buildOfferValidity(offer: {
    startDate?: string;
    endDate?: string;
  }): string {
    const start = offer.startDate || '';
    const end = offer.endDate || '';
    if (start && end) {
      return `${start} - ${end}`;
    }
    return start || end || '';
  }

  private clearAvailablePromotions(): void {
    this.promotionDialogOffers = [];
    this.promotionDialogSelection = [];
    if (this.selectedOfferIds.length > 0) {
      this.selectedOfferIds = [];
      this.selectedOfferLabels = [];
    }
    Object.assign(this.formData, {
      selectedPromotionOffers: [],
      selectedPromotionOffersDisplay: [],
    });
    this.cdr.detectChanges();
  }

  private loadAvailablePromotions(): void {
    if (this.promotionAvailableSub) {
      this.promotionAvailableSub.unsubscribe();
      this.promotionAvailableSub = null;
    }

    if (!this.hasPromotionCheckoutContext()) {
      this.showPromotionContextWarning();
      return;
    }

    const dto = this.buildPromotionOrderTableDTO(undefined, {
      forPreview: true,
    });
    if (!dto) {
      this.showPromotionContextWarning();
      return;
    }

    this.showPromotionSelectionDialog = true;
    this.promotionDialogLoading = true;
    this.promotionAvailableSub = this.promotionCheckout
      .listAvailableForOrder(dto, 'body', false, {
        httpHeaderAccept: 'application/json' as any,
      })
      .pipe(
        catchError((err) => {
          console.error('Load available promotions failed:', err);
          return of({ offers: [] });
        }),
        finalize(() => {
          this.promotionDialogLoading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe((res) => {
        const offers = this.extractAvailableOffers(res);
        this.promotionDialogOffers = offers
          .filter(
            (o) =>
              o.offerId != null && o.requiresEnteredPromotionCode === false,
          )
          .map((o) => ({
            offerId: o.offerId as number,
            offerCode: o.code || String(o.offerId),
            offerName: o.displayName || o.code || String(o.offerId),
            description: o.shortDescription || '',
            validity: this.buildOfferValidity(o),
            priority: o.priority ?? null,
          }));
        const selectedIds = new Set(this.selectedOfferIds);
        this.promotionDialogSelection = this.promotionDialogOffers.filter((o) =>
          selectedIds.has(o.offerId),
        );
        this.cdr.detectChanges();
      });
  }

  private showPromotionContextWarning(): void {
    this.messageService.add({
      severity: 'warn',
      summary: this.translateService.instant('common.messages.warning'),
      detail: this.translateService.instant(
        'o2o.create.form.messages.promotion_context_required',
      ),
      life: 4000,
    });
  }

  private refreshPromotionPreviewAndSummary(): void {
    void this.runPromotionPreviewAndSummary();
  }

  private async runPromotionPreviewAndSummary(): Promise<void> {
    this.couponDiscountAmount =
      await this.fetchPromotionPreviewTotalDiscount();
    this.updateSummary();
  }

  private setupCouponInputSubscription(): void {
    // Auto-apply coupon subscription has been intentionally disabled.
    // Coupon handling is done via p-autocomplete completeMethod (search) and onSelect/onUnselect (apply/remove).
    return;
  }

  onCouponSearch(event: { query: string; field: string }): void {
    if (event.field !== 'couponDiscount') {
      return;
    }
    const query = (event.query || '').trim();
    if (!query) {
      this.couponSuggestions = {};
      this.addFormComponent?.clearCouponSuggestions('couponDiscount');
      return;
    }

    // Cancel any in-flight search/validation
    if (this.couponSearchSub) {
      this.couponSearchSub.unsubscribe();
      this.couponSearchSub = null;
    }

    const applied = new Set(
      (this.appliedCoupons || []).map((c) =>
        (c?.couponCode || '').toString().trim().toUpperCase(),
      ),
    );

    this.couponSearchSub = this.searchCoupons(query)
      .pipe(
        map((coupons) =>
          (coupons || []).filter((c) => {
            const code = (c?.couponCode || '').toString().trim().toUpperCase();
            return code && !applied.has(code);
          }),
        ),
        switchMap((filtered) => {
          const candidates = filtered.length
            ? filtered
            : [{ couponCode: query }];
          const validations = candidates.map((c) =>
            this.validateIssuedCoupon(c),
          );
          return forkJoin(validations).pipe(
            map((results) => (results || []).filter((x) => !!x)),
          );
        }),
        catchError((err) => {
          console.error('Error during coupon search/validation:', err);
          return of([]);
        }),
      )
      .subscribe((validCoupons: any[]) => {
        this.couponSuggestions = {
          couponDiscount: validCoupons,
        };
        this.addFormComponent?.updateCouponSuggestions(
          'couponDiscount',
          validCoupons,
        );
        this.cdr.detectChanges();
      });
  }

  onCouponSelect(event: { selected: any; field: string }): void {
    if (event.field !== 'couponDiscount') {
      return;
    }

    const selected = event.selected;
    const coupon = selected?.value ?? selected;
    if (!coupon || !coupon.couponCode) {
      return;
    }

    const codeNormalized = coupon.couponCode.toString().trim().toUpperCase();
    const isDuplicate = this.appliedCoupons.some(
      (c) =>
        (c.couponCode || '').toString().trim().toUpperCase() === codeNormalized,
    );
    if (isDuplicate) {
      // Silently ignore duplicates (no toast). Also remove any duplicate chip that PrimeNG may try to add.
      const ctrl = this.addFormComponent?.form?.get('couponDiscount');
      if (ctrl) {
        const current: any[] = Array.isArray(ctrl.value) ? ctrl.value : [];
        ctrl.setValue(
          current.filter(
            (x) =>
              (x?.couponCode || '').toString().trim().toUpperCase() !==
              codeNormalized,
          ),
          { emitEvent: false },
        );
      }
      return;
    }

    this.appliedCoupons = [...this.appliedCoupons, coupon];
    this.couponInputError = null;
    this.couponSuggestions = {};
    this.addFormComponent?.clearCouponSuggestions('couponDiscount');
    this.refreshPromotionPreviewAndSummary();
  }

  onCouponUnselect(event: { removed: any; field: string }): void {
    if (event.field !== 'couponDiscount') {
      return;
    }
    const removed = event.removed?.value ?? event.removed;
    const code = removed?.couponCode;
    if (!code) {
      return;
    }
    this.removeCouponByCode(code);
  }

  /**
   * Remove coupon chip and re-enable input
   * Completely resets the field to a blank, editable state
   */
  removeCoupon(): void {
    // Set flag to prevent re-validation during removal
    this.isRemovingCoupon = true;

    // Get form control reference FIRST to check initial state
    let couponControl: any = null;
    if (this.addFormComponent && this.addFormComponent.form) {
      couponControl = this.addFormComponent.form.get('couponDiscount');
    }

    // Clear all coupon-related state FIRST (remove all coupons)
    this.selectedCoupon = null;
    this.appliedCoupons = [];
    this.couponInputError = null;
    this.lastValidatedCode = null;

    // Unlock field configuration BEFORE clearing value
    this.updateCouponFieldLockState(false);

    // Completely reset form control to empty string
    if (couponControl) {
      // Disable temporarily to prevent valueChanges from firing during reset
      const wasEnabled = !couponControl.disabled;
      couponControl.disable({ emitEvent: false });

      // Use reset() to completely reset the control (more aggressive than setValue)
      couponControl.reset([], { emitEvent: false });

      couponControl.setErrors(null);
      couponControl.markAsPristine();
      couponControl.markAsUntouched();

      // Re-enable if table has items and field should be enabled
      const hasItems =
        this.tableSections[0].data && this.tableSections[0].data.length > 0;
      if (hasItems && wasEnabled) {
        couponControl.enable({ emitEvent: false });
        // Reset again after enabling to ensure it's cleared
        couponControl.reset([], { emitEvent: false });
        couponControl.setValue([], { emitEvent: false });
      }

      // Force update to ensure UI reflects the change
      couponControl.updateValueAndValidity({ emitEvent: false });
    } else {
      console.warn('[removeCoupon] WARNING: couponControl is null!');
    }

    // DON'T re-setup subscription immediately - wait until we're sure value is cleared
    // This prevents p-autocomplete from re-triggering validation with old value

    Object.assign(this.formData, { couponDiscount: [] });
    this.refreshPromotionPreviewAndSummary();

    // Force change detection to update UI immediately
    this.cdr.detectChanges();

    // Direct DOM manipulation as fallback to clear p-autocomplete input
    // This ensures the visual input is cleared even if p-autocomplete's internal state persists
    // Do this while isRemovingCoupon flag is still true to prevent valueChanges from processing
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (this.addFormComponent && this.addFormComponent.form) {
          const inputElement = document.querySelector(
            'input[formcontrolname="couponDiscount"]',
          ) as HTMLInputElement;
          if (inputElement && inputElement.value) {
            // Clear the input value directly
            inputElement.value = '';
            // Also clear the form control to ensure sync
            if (couponControl) {
              couponControl.setValue([], { emitEvent: false });
            }
          }
        }
      }, 0);
    });

    // Use requestAnimationFrame to ensure DOM is updated, then clear again
    requestAnimationFrame(() => {
      setTimeout(() => {
        // Double-check that value is cleared after change detection
        if (couponControl) {
          const currentValue = couponControl.value;
          if (
            currentValue !== '' &&
            currentValue !== null &&
            currentValue !== undefined
          ) {
            couponControl.reset([], { emitEvent: false });
            couponControl.setValue([], { emitEvent: false });
            couponControl.patchValue([], { emitEvent: false });
            couponControl.updateValueAndValidity({ emitEvent: false });
          }
        }
        this.cdr.detectChanges();
      }, 0);
    });

    // Final check after a longer delay to catch any async updates
    setTimeout(() => {
      if (couponControl) {
        const currentValue = couponControl.value;
        if (
          currentValue !== '' &&
          currentValue !== null &&
          currentValue !== undefined
        ) {
          console.error(
            '[removeCoupon] setTimeout(100) - ERROR: Value STILL exists after 100ms!',
            currentValue,
          );
          // Use reset() for more aggressive clearing
          couponControl.reset([], { emitEvent: false });
          couponControl.setValue([], { emitEvent: false });
          couponControl.patchValue([], { emitEvent: false });
          couponControl.updateValueAndValidity({ emitEvent: false });
          this.cdr.detectChanges();

          // One more attempt after another delay
          setTimeout(() => {
            if (
              couponControl.value !== '' &&
              couponControl.value !== null &&
              couponControl.value !== undefined
            ) {
              console.error(
                '[removeCoupon] setTimeout(200) - ERROR: Value STILL exists! Final aggressive clear...',
              );
              couponControl.reset([], { emitEvent: false });
              couponControl.setValue([], { emitEvent: false });
              this.cdr.detectChanges();
            }
          }, 100);
        }
      }

      // Clear the removal flag and re-setup subscription AFTER ensuring value is cleared
      this.isRemovingCoupon = false;

      // Auto-apply subscription intentionally not restarted.
    }, 100);
  }

  /**
   * Remove a specific coupon chip by code; keeps input editable and intact
   */
  removeCouponByCode(code: string): void {
    // Set flag to prevent re-validation during removal
    this.isRemovingCoupon = true;

    // Remove only the specified coupon
    const beforeCount = this.appliedCoupons.length;
    this.appliedCoupons = this.appliedCoupons.filter(
      (c) => c.couponCode !== code,
    );
    if (this.appliedCoupons.length !== beforeCount) {
      this.refreshPromotionPreviewAndSummary();
    }

    // Ensure no error state remains
    this.couponInputError = null;
    this.lastValidatedCode = null;

    // Ensure field stays editable; no auto-apply subscription
    setTimeout(() => {
      this.isRemovingCoupon = false;
      this.cdr.detectChanges();
    }, 100);
  }

  /**
   * Load existing coupon for edit mode
   * Call this method when editing an order that already has a coupon
   */
  loadExistingCoupon(couponCode: string, couponData?: any): void {
    if (!couponCode) {
      return;
    }

    // If coupon data is provided, use it; otherwise validate the code
    if (couponData) {
      const exists = this.appliedCoupons.some(
        (c) => c.couponCode === couponCode,
      );
      if (!exists) {
        this.appliedCoupons = [...this.appliedCoupons, couponData];
      }
      this.updateSummary();
    } else {
      // Validate the coupon code
      this.validateCouponCode(couponCode).subscribe({
        next: (coupon) => {
          if (coupon) {
            const exists = this.appliedCoupons.some(
              (c) => c.couponCode === coupon.couponCode,
            );
            if (!exists) {
              this.appliedCoupons = [...this.appliedCoupons, coupon];
            }
            this.updateSummary();
            this.cdr.detectChanges();
          }
        },
      });
    }
  }

  /**
   * Update coupon field lock state based on whether a coupon is selected
   */
  private updateCouponFieldLockState(locked: boolean): void {
    const couponField = this.formFields.find(
      (field) => field.controlName === 'couponDiscount',
    );

    if (couponField) {
      couponField.disabled = locked;
      couponField.readonly = locked;
      // Trigger change detection
      this.formFields = [...this.formFields];
    }

    // Update form control state
    if (this.addFormComponent && this.addFormComponent.form) {
      const couponControl = this.addFormComponent.form.get('couponDiscount');
      if (couponControl) {
        if (locked) {
          // Disable and clear value when locked
          couponControl.disable({ emitEvent: false });
          couponControl.setValue(null, { emitEvent: false });
        } else {
          // Only enable if table has items
          // Note: The actual value reset is handled by removeCoupon() to ensure complete reset
          const hasItems =
            this.tableSections[0].data && this.tableSections[0].data.length > 0;
          if (hasItems) {
            couponControl.enable({ emitEvent: false });
            // Clear value when unlocking to ensure field is blank
            couponControl.setValue([], { emitEvent: false });
          }
        }
      }
    }
  }

  private setDefaultValues() {
    // Set default values for form fields

    const defaultValues: { [key: string]: any } = {
      // Set default date for O2O orders
      thirdPartyOrderTime: new Date(),
      paymentStatus: 0, // Unpaid by default
      takingDeliveryWay: O2O_TAKING_DELIVERY_WAY.pickup,
      actionType: 'CREATE',
      businessType: 'O2O',
      paymentAmount: 0,
      totalProductAmount: 0,
      costTotalAmount: 0,
      salesChannel: 'website', // Default to official website
      cod: 0,
      declaredValue: 0,
      packageLength: 0,
      packageWidth: 0,
      packageHeight: 0,
      packageWeight: 0,
      selectedPromotionOffers: [],
      selectedPromotionOffersDisplay: [],
    };

    // Initialize formData with default values
    this.formData = { ...defaultValues };

    // Update form fields with default values
    this.formFields.forEach((field) => {
      if (defaultValues[field.controlName] !== undefined) {
        field.defaultValue = defaultValues[field.controlName];
      }
    });
  }

  /**
   * Set maxDate to current date for Order Date and Payment Date fields
   * to prevent users from selecting future dates
   */
  private setMaxDateForDateFields(): void {
    const currentDate = new Date();
    const orderDateField = this.formFields.find(
      (field) => field.controlName === 'thirdPartyOrderTime',
    );
    const paymentDateField = this.formFields.find(
      (field) => field.controlName === 'paymentTime',
    );

    if (orderDateField) {
      orderDateField.maxDate = currentDate;
    }

    if (paymentDateField) {
      paymentDateField.maxDate = currentDate;
    }
  }

  onFormAction(event: { action: string; data?: any }): void {
    switch (event.action) {
      case 'save':
        if (this.loading) {
          return;
        }
        void this.onSave(event.data);
        break;
      case 'cancel':
        this.onCancel();
        break;
      case 'takingDeliveryWayChange':
        // Handle takingDeliveryWay change from layout component
        if (event.data?.takingDeliveryWay !== undefined) {
          this.formData.takingDeliveryWay = event.data.takingDeliveryWay;
          this.onFormValueChanges({
            takingDeliveryWay: event.data.takingDeliveryWay,
          });
        }
        break;
      case 'formValueChange':
        // Handle form value changes (e.g., provinceId)
        if (event.data) {
          // Update formData
          Object.assign(this.formData, event.data);
          // Trigger onFormValueChanges to handle side effects
          this.onFormValueChanges(event.data);
        }
        break;
    }
  }

  onTableAction(event: { section: string; action: string; data?: any }): void {
    switch (event.action) {
      case 'addProduct':
        this.addProduct();
        break;
      case 'addPackage':
        this.addPackageProduct();
        break;
      case 'deleteSelected':
        this.deleteSelectedItems(event.data || []);
        break;
      case 'selectionChange':
        // Update the table selection
        this.tableSections[0].selection = event.data || [];
        this.updateTableActions(event.data || []);
        this.updateSummary(); // Update summary when selection changes
        this.cdr.detectChanges();
        break;
      case 'cellValueChange':
        // Handle quantity or price changes
        this.onTableValueChange(event.data);
        break;
    }
  }

  onSectionAction(event: { section: string; action: string; data?: any }): void {
    if (event.action === 'findEligiblePromotions') {
      this.loadAvailablePromotions();
    }
  }

  onPromotionDialogVisibleChange(visible: boolean): void {
    this.showPromotionSelectionDialog = visible;
    if (!visible) {
      this.promotionDialogSelection = [];
    }
  }

  onPromotionDialogSelectionChange(
    selection: Array<{
      offerId: number;
      offerCode: string;
      offerName: string;
      description: string;
      validity: string;
      priority: number | null;
    }>,
  ): void {
    this.promotionDialogSelection = selection || [];
  }

  onPromotionDialogConfirm(): void {
    const selected = this.promotionDialogSelection || [];
    this.selectedOfferIds = selected.map((offer) => offer.offerId);
    this.selectedOfferLabels = selected.map((offer) => offer.offerName);
    this.selectedPromotionOfferChips = selected.map((offer) => ({
      offerId: offer.offerId,
      offerName: offer.offerName,
    }));
    Object.assign(this.formData, {
      selectedPromotionOffers: [...this.selectedOfferIds],
      selectedPromotionOffersDisplay: [...this.selectedPromotionOfferChips],
    });
    this.addFormComponent?.form?.patchValue(
      {
        selectedPromotionOffers: [...this.selectedOfferIds],
        selectedPromotionOffersDisplay: [...this.selectedPromotionOfferChips],
      },
      { emitEvent: false },
    );
    this.addFormComponent?.notifyFormFieldsUpdated();
    this.showPromotionSelectionDialog = false;
    this.refreshPromotionPreviewAndSummary();
  }

  /**
   * Add product via selection dialog
   */
  private addProduct() {
    this.loadProductsForDialog();
    this.showProductSelectionDialog = true;
  }

  /**
   * Load products for the dialog
   */
  private loadProductsForDialog(searchParams?: Record<string, string>) {
    this.productDialogLoading = true;

    const params: ProductSearchParams = {
      page: this.productDialogCurrentPage,
      size: this.productDialogPageSize,
      // Map product-name dialog filter to skuName (avoids spuName ScriptSort 500; RBA-1068)
      ...(searchParams?.['spuName'] && { skuName: searchParams['spuName'] }),
      ...(searchParams?.['spuCode'] && { spuCode: searchParams['spuCode'] }),
      ...(searchParams?.['skuCode'] && { skuCode: searchParams['skuCode'] }),
      sortBy: PRODUCT_SERVICE_CONFIG.defaultSortBy,
      ascending: PRODUCT_SERVICE_CONFIG.defaultAscending,
    };

    this.productService.searchProducts(params).subscribe({
      next: (result) => {
        // Ensure we have products array
        const products = result?.products || [];

        // Mark products as disabled if they're already selected
        // Create a completely new array reference to ensure change detection works with OnPush
        const mappedProducts = products.map((product: any) => ({
          ...product,
          disable: this.alreadySelectedSkuCodes.has(product.skuCode) ? 1 : 0,
        }));

        // Assign new array reference to trigger change detection in OnPush child component
        this.productDialogProducts = [...mappedProducts];
        this.productDialogTotalRecords = result?.totalRecords || 0;
        this.productDialogLoading = false;

        // Force change detection
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error loading products for dialog:', error);
        this._productDialogProducts = [];
        this.productDialogTotalRecords = 0;
        this.productDialogLoading = false;
        this.cdr.detectChanges();
      },
    });
  }

  private deleteSelectedItems(selectedItems: any[]) {
    // Remove selected items from the table data
    this.tableSections[0].data = this.tableSections[0].data.filter(
      (item) => !selectedItems.some((selected) => selected.id === item.id),
    );
    // Clear selection after deletion
    this.tableSections[0].selection = [];

    // Create new reference to trigger change detection
    this.tableSections = [...this.tableSections];

    // Update already selected SKU codes
    this.updateAlreadySelectedSkuCodes();

    this.updateTableActions([]);
    this.updateSummary();
    this.updateCouponFieldState();

    // Try to call nearest warehouse API (will check conditions internally)
    this.tryCallNearestWarehouse();
    this.requestPackageConversionDetection();

    this.cdr.detectChanges();
  }

  private updateTableActions(selection: any[]) {
    const deleteAction = this.tableSections[0].actions?.find(
      (a) => a.action === 'deleteSelected',
    );

    if (deleteAction) {
      // Disable delete button if no items in table OR no items selected
      deleteAction.disabled = selection.length === 0;
    }
  }

  /**
   * Handle table value changes (quantity, price, etc.)
   */
  onTableValueChange(data: any): void {
    if (!data || !data.rowData) return;

    const item = data.rowData;
    const field = data.prop;
    const value = data.value;

    // Find the item in the table data
    const itemIndex = this.tableSections[0].data.findIndex(
      (tableItem: any) => tableItem.id === item.id,
    );
    if (itemIndex === -1) return;

    // Create updated item with new field value
    const updatedItem = {
      ...this.tableSections[0].data[itemIndex],
      [field]: value,
    };

    if (field === 'quantity') {
      this.updatePackageChildQuantities(updatedItem);
    }

    // Recalculate totals based on the changed field
    this.updateItemTotals(updatedItem);

    // Update summary immediately after quantity change
    this.updateSummary();

    // Try to call nearest warehouse API if quantity changed (will check conditions internally)
    this.tryCallNearestWarehouse();
    if (field === 'quantity') {
      this.requestPackageConversionDetection();
    }

    // Force change detection
    this.cdr.detectChanges();
  }

  /**
   * Update item totals based on quantity and prices
   */
  private updateItemTotals(item: any): void {
    const quantity = item.quantity || 1;
    const costPrice = item.costPrice || 0;
    const thirdPartyPrice = item.thirdPartyPrice || 0;
    const internalPrice = item.internalPrice || 0;
    const shippingFee = item.shippingFee || 0;

    // Calculate all totals based on quantity
    item.costTotal = costPrice * quantity;
    item.thirdPartyTotal = thirdPartyPrice * quantity;
    item.internalTotal = internalPrice * quantity;
    item.shippingFee = shippingFee; // Keep shipping fee as is (per item or total)

    // Update the item in the array and create new reference to trigger change detection
    const itemIndex = this.tableSections[0].data.findIndex(
      (tableItem: any) => tableItem.id === item.id,
    );
    if (itemIndex !== -1) {
      // Create a completely new array with the updated item
      const newData = this.tableSections[0].data.map((tableItem, index) =>
        index === itemIndex ? { ...item } : tableItem,
      );

      // Create a completely new tableSections array
      this.tableSections = [
        {
          ...this.tableSections[0],
          data: newData,
        },
      ];
    }
  }

  private updatePackageChildQuantities(item: {
    quantity?: number;
    packageChildren?: PackageSkuChild[];
  }): void {
    if (!item.packageChildren?.length) {
      return;
    }

    const packageQuantity = Number(item.quantity) || 1;
    item.packageChildren = item.packageChildren.map((child) => ({
      ...child,
      quantity: packageQuantity * child.quantityPerPackage,
    }));
  }

  private updateSummary() {
    // Clear any pending timeout to debounce multiple calls
    if (this.updateSummaryTimeout) {
      clearTimeout(this.updateSummaryTimeout);
    }

    // Debounce summary updates to prevent multiple rapid calls
    this.updateSummaryTimeout = setTimeout(() => {
      this.performSummaryUpdate();
    }, 50); // Reduced debounce time for better responsiveness
  }

  /**
   * Perform the actual summary update (extracted from updateSummary)
   */
  private performSummaryUpdate(): void {
    this.renderSummaryTotals();
  }

  /** Update summary UI after promotion preview returns discount. */
  private renderSummaryTotals(): void {
    const allItems = this.tableSections[0].data || [];
    const totalItems = allItems.length;
    const goodsTotalAmount = allItems.reduce(
      (sum, item) => sum + (item.internalTotal || 0),
      0,
    );
    const shippingAmount = this.formData.shippingAmount || 0;
    const costTotalAmount = allItems.reduce(
      (sum, item) => sum + (item.costPrice || 0),
      0,
    );
    const sellingTotalAmount = allItems.reduce(
      (sum, item) => sum + (item.thirdPartyTotal || 0),
      0,
    );
    const totalDiscount = this.hasPromotionCheckoutSelection()
      ? this.couponDiscountAmount || 0
      : 0;
    const paymentAmount = sellingTotalAmount + shippingAmount - totalDiscount;

    this.summarySection.items = [
      {
        ...this.summarySection.items[0],
        value: totalItems,
        formatter: this.summarySection.items[0].formatter || 'number',
      },
      {
        ...this.summarySection.items[1],
        value: goodsTotalAmount,
        formatter: this.summarySection.items[1].formatter || 'currency',
        currency: this.summarySection.items[1].currency || 'VND',
      },
      {
        ...this.summarySection.items[2],
        value: shippingAmount,
        formatter: this.summarySection.items[2].formatter || 'currency',
        currency: this.summarySection.items[2].currency || 'VND',
      },
      {
        ...this.summarySection.items[3],
        value: costTotalAmount,
        formatter: this.summarySection.items[3].formatter || 'currency',
        currency: this.summarySection.items[3].currency || 'VND',
      },
      {
        ...this.summarySection.items[4],
        value: totalDiscount,
        formatter: this.summarySection.items[4].formatter || 'currency',
        currency: this.summarySection.items[4].currency || 'VND',
      },
      {
        ...this.summarySection.items[5],
        value: paymentAmount,
        formatter: this.summarySection.items[5].formatter || 'currency',
        currency: this.summarySection.items[5].currency || 'VND',
      },
    ];

    this.summarySection = { ...this.summarySection };

    Object.assign(this.formData, {
      goodsTotalAmount,
      costTotalAmount,
      couponDiscountAmount: totalDiscount,
      totalDiscount,
      paymentAmount,
      shippingAmount,
    });

    if (this.addFormComponent?.form) {
      this.addFormComponent.form.patchValue({
        goodsTotalAmount,
        costTotalAmount,
        couponDiscountAmount: this.couponDiscountAmount,
        paymentAmount,
        shippingAmount,
      });
    }

    this.cdr.detectChanges();
  }

  /**
   * Handle product selection confirmation
   */
  onProductSelectionConfirm(selectedProducts: ProductItem[]): void {
    if (selectedProducts.length === 0) {
      return;
    }

    // Get existing SKU codes to prevent duplicates
    const existingSkuCodes = new Set(
      this.tableSections[0].data.map((item: any) => item.skuCode),
    );

    // Filter out products that are already added
    const validProducts = selectedProducts.filter(
      (product) => !existingSkuCodes.has(product.skuCode),
    );

    // Convert valid products to order items
    const newOrderItems = validProducts.map((product, index) => {
      const costPrice = product.displayPrice || 0;
      const quantity = 1;

      return {
        id: Date.now() + index,
        orderItemCode: `ITEM-${Date.now()}-${index + 1}`,
        skuCode: product.skuCode,
        skuId: product.skuId,
        skuName: product.skuName,
        spuName: product.spuName,
        spuCode: product.spuCode,
        quantity: quantity,
        thirdPartyPrice: product.displayPrice || product.salesPrice || 0,
        thirdPartyTotal:
          (product.displayPrice || product.salesPrice || 0) * quantity,
        internalPrice: product.salesPrice || 0,
        internalTotal: (product.salesPrice || 0) * quantity,
        costPrice: costPrice,
        costTotal: costPrice * quantity,
        giftFlag: false, // Use giftFlag instead of isGift
        spuId: product.spuId,
        brandName: product.brandName,
        categoryName: product.categoryName,
        setFlag: product.setFlag ?? 0,
        isPackage: product.isPackage ?? product.setFlag === 1,
        fulfillmentMode: product.fulfillmentMode,
        shippingFee: 0,
        type: 'product',
      };
    });

    // Add new items to table - create new reference to trigger change detection
    this.tableSections[0].data = [
      ...this.tableSections[0].data,
      ...newOrderItems,
    ];

    // Create a completely new tableSections array to force Angular change detection
    this.tableSections = [...this.tableSections];

    this.loadPackageChildren(newOrderItems);

    // Update already selected SKU codes
    this.updateAlreadySelectedSkuCodes();

    // Update table actions and summary (no auto-selection)
    this.updateTableActions([]);
    this.updateSummary();
    this.updateCouponFieldState();
    this.error = null;

    // Try to call nearest warehouse API if province/ward already exist
    this.tryCallNearestWarehouse();
    this.requestPackageConversionDetection();

    this.cdr.detectChanges();
  }

  /**
   * Loads every Package SKU's components as nested display data. This gives the
   * operator the exact BOM for both Virtual Bundle and Pre-Packed packages;
   * children are not order rows, so totals, selection, payloads, and
   * fulfillment behavior remain unchanged.
   */
  private loadPackageChildren(
    orderItems: Array<{
      skuCode?: string;
      setFlag?: number;
      isPackage?: boolean;
      fulfillmentMode?: string;
    }>,
  ): void {
    const packageSkuCodes = orderItems
      .filter((item) => this.isPackageOrderItem(item))
      .map((item) => item.skuCode)
      .filter((skuCode): skuCode is string => Boolean(skuCode));

    if (!packageSkuCodes.length) {
      return;
    }

    this.goodsSkuController.getMealsGoodsList(
      packageSkuCodes,
      'body',
      false,
      { httpHeaderAccept: 'application/json' as any },
    ).pipe(
      catchError(() => of([] as RpcOrderResVO[])),
    ).subscribe((components) => {
      if (!Array.isArray(components)) {
        return;
      }

      const childrenByPackageSku = new Map<string, PackageSkuChild[]>();

      components.forEach((component) => {
        if (!component.mainSkuCode || !component.skuCode) {
          return;
        }

        const packageSkuKey = this.toSkuKey(component.mainSkuCode);
        const packageChildren = childrenByPackageSku.get(packageSkuKey) ?? [];
        packageChildren.push({
          skuId: component.skuId,
          spuCode: component.spuCode ?? '',
          spuName: component.spuName ?? '',
          skuCode: component.skuCode,
          skuName: component.skuName ?? '',
          quantityPerPackage: component.num ?? 0,
          quantity: component.num ?? 0,
        });
        childrenByPackageSku.set(packageSkuKey, packageChildren);
      });

      if (!childrenByPackageSku.size) {
        return;
      }

      const newData = this.tableSections[0].data.map((item: any) => {
        const packageChildren = childrenByPackageSku.get(
          this.toSkuKey(item.skuCode),
        );
        if (!packageChildren) {
          return item;
        }

        const updatedItem = { ...item, packageChildren };
        this.updatePackageChildQuantities(updatedItem);
        return updatedItem;
      });

      this.tableSections = this.tableSections.map((section, index) =>
        index === 0 ? { ...section, data: newData } : section,
      ) as typeof this.tableSections;
      this.cdr.markForCheck();
    });
  }

  private toSkuKey(skuCode: unknown): string {
    return String(skuCode ?? '').trim().toUpperCase();
  }

  /**
   * Handle product selection dialog visibility change
   */
  onProductSelectionDialogVisibleChange(visible: boolean): void {
    this.showProductSelectionDialog = visible;
    if (!visible) {
      // Reset dialog state when closed
      this._productDialogProducts = [];
      this.productDialogTotalRecords = 0;
      this.productDialogCurrentPage = 1;
      this.productDialogSearchParams = {};
    }
  }

  /**
   * Handle product search in dialog
   */
  onProductSearch(searchParams: Record<string, string>): void {
    this.productDialogCurrentPage = 1;
    // Store search parameters to maintain filters when changing pages
    this.productDialogSearchParams = { ...searchParams };
    this.loadProductsForDialog(searchParams);
  }

  /**
   * Handle product search reset in dialog
   */
  onProductSearchReset(): void {
    this.productDialogCurrentPage = 1;
    // Clear stored search parameters
    this.productDialogSearchParams = {};
    this.loadProductsForDialog();
  }

  /**
   * Handle product page change in dialog
   */
  onProductPageChange(page: number): void {
    this.productDialogCurrentPage = page;
    // Reuse stored search parameters when changing pages
    this.loadProductsForDialog(this.productDialogSearchParams);
  }

  /**
   * Handle product search from search field (autocomplete)
   */
  onProductSearchField(event: { query: string }): void {
    if (!event?.query || event.query.trim().length < 1) {
      this.addFormComponent?.updateProductSearchSuggestions([]);
      return;
    }

    const params: ProductSearchParams = {
      page: 1,
      size: 10, // Limit to 10 suggestions for autocomplete
      // Prefer skuName over spuName: avoids goods ES ScriptSort path that 500s when
      // EsQueryCompat drops script params (RBA-3204 / RBA-1068). skuName already works on Dev.
      skuName: event.query.trim(),
      sortBy: PRODUCT_SERVICE_CONFIG.defaultSortBy,
      ascending: PRODUCT_SERVICE_CONFIG.defaultAscending,
    };

    this.productService.searchProducts(params).subscribe({
      next: (result) => {
        // Filter out already selected products and map to ProductItem format (same as dialog)
        const existingSkuCodes = new Set(
          this.tableSections[0].data.map((item: any) => item.skuCode),
        );
        const availableProducts = result.products
          .filter((product: any) => !existingSkuCodes.has(product.skuCode))
          .map((product: any) => ({
            ...product,
            disable: this.alreadySelectedSkuCodes.has(product.skuCode) ? 1 : 0,
          }));
        this.addFormComponent?.updateProductSearchSuggestions(
          availableProducts,
        );
      },
      error: (error) => {
        console.error('Error searching products:', error);
        this.addFormComponent?.updateProductSearchSuggestions([]);
      },
    });
  }

  /**
   * Handle product selection from search field
   */
  onProductSearchSelect(event: { product: any }): void {
    if (!event?.product) {
      return;
    }

    // Extract the actual product from the nested structure (p-autocomplete returns {value: {...}})
    const product = event.product.value || event.product;

    // Add to productDialogProducts array for consistency (same as dialog)
    const productItem: ProductItem = {
      ...product,
      disable: this.alreadySelectedSkuCodes.has(product.skuCode) ? 1 : 0,
    };

    // Add to productDialogProducts if not already present
    const existingIndex = this.productDialogProducts.findIndex(
      (p) => p.skuCode === product.skuCode,
    );
    if (existingIndex === -1) {
      this.productDialogProducts = [...this.productDialogProducts, productItem];
    } else {
      // Update existing product
      this.productDialogProducts = this.productDialogProducts.map((p, idx) =>
        idx === existingIndex ? productItem : p,
      );
    }

    // Use the same logic as onProductSelectionConfirm but for a single product
    // Convert single product to array format to reuse the same logic
    const selectedProducts: ProductItem[] = [productItem];
    this.onProductSelectionConfirm(selectedProducts);
    // Note: callNearestWarehouseAPI is already called in onProductSelectionConfirm
  }

  private async onSave(formValue: any) {
    if (this.loading) {
      return;
    }
    this.loading = true;
    this.error = null;

    // Get the current form values directly from the form component
    // Important: Read from initialData (which is bound to formData) to get all current values
    // including buyer/receiver fields that might have been updated via ngModel
    let actualFormValue = formValue;
    if (this.addFormComponent) {
      // Get values from initialData (which should have all current form values)
      const initialDataValues = this.addFormComponent.initialData || {};
      // Also get values from form.value as fallback
      const formValues = this.addFormComponent.form?.value || {};
      // Merge: formData (base) -> initialData (current) -> form.value (fallback) -> formValue (event)
      actualFormValue = {
        ...this.formData,
        ...initialDataValues,
        ...formValues,
        ...actualFormValue,
      };
    } else {
      // Fallback to formData if form component is not available
      actualFormValue = { ...this.formData, ...actualFormValue };
    }

    // Validate visible required form fields before building the BE payload.
    const missingFields = this.getMissingRequiredFormFieldLabels(
      actualFormValue,
    );

    // Stop save if required fields are missing
    if (missingFields.length > 0) {
      this.loading = false;
      // Mark all form fields as touched to show validation errors
      if (this.addFormComponent?.form?.controls) {
        const controls = Object.values(
          this.addFormComponent.form.controls,
        ) as AbstractControl[];
        controls.forEach((control) => {
          if (control && typeof control.markAsTouched === 'function') {
            control.markAsTouched();
          }
        });
      }
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant(
          'o2o.messages.please_fill_required_fields',
        ),
        life: 5000,
      });
      return;
    }

    // Số Nhà/Tên Đường only required when delivery method is ship-to-address
    const takingDeliveryWayForAddress =
      actualFormValue.takingDeliveryWay || O2O_TAKING_DELIVERY_WAY.pickup;
    const isShipToAddress =
      takingDeliveryWayForAddress === O2O_TAKING_DELIVERY_WAY.delivery ||
      takingDeliveryWayForAddress === 'SHIP_TO_ADDRESS';
    if (
      isShipToAddress &&
      (!actualFormValue.shippingAddress ||
        actualFormValue.shippingAddress.trim() === '')
    ) {
      this.loading = false;
      const addressControl =
        this.addFormComponent?.form?.get('shippingAddress');
      if (
        addressControl &&
        typeof addressControl.markAsTouched === 'function'
      ) {
        addressControl.markAsTouched();
      }
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant(
          'o2o.create.form.validation.shipping_address_required',
        ),
        life: 5000,
      });
      return;
    }

    const allItems = this.tableSections[0].data || [];

    // Validate that there are items in the table
    if (allItems.length === 0) {
      this.loading = false;
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant(
          'o2o.create.form.validation.no_products_selected',
        ),
        life: 5000,
      });
      return;
    }

    // Validate Order Date and Payment Date are not in the future
    const currentDate = new Date();
    currentDate.setHours(23, 59, 59, 999); // Set to end of current day for comparison

    if (actualFormValue.thirdPartyOrderTime) {
      const orderDate = new Date(actualFormValue.thirdPartyOrderTime);
      if (orderDate > currentDate) {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.messages.error'),
          detail: this.translateService.instant(
            'o2o.create.form.validation.order_date_future_not_allowed',
          ),
          life: 5000,
        });
        return;
      }
    }

    if (actualFormValue.paymentTime) {
      const paymentDate = new Date(actualFormValue.paymentTime);
      if (paymentDate > currentDate) {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.messages.error'),
          detail: this.translateService.instant(
            'o2o.create.form.validation.payment_date_future_not_allowed',
          ),
          life: 5000,
        });
        return;
      }

      // Validate payment date is not earlier than order date
      if (actualFormValue.thirdPartyOrderTime) {
        const orderDate = new Date(actualFormValue.thirdPartyOrderTime);
        orderDate.setHours(0, 0, 0, 0);
        const paymentDateOnly = new Date(paymentDate);
        paymentDateOnly.setHours(0, 0, 0, 0);

        if (paymentDateOnly < orderDate) {
          this.loading = false;
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'o2o.create.form.validation.payment_date_earlier_than_order_date',
            ),
            life: 5000,
          });
          return;
        }
      }
    }

    const invalidItems = allItems.filter(
      (item) => !item.skuId || !item.skuCode,
    );

    if (invalidItems.length > 0) {
      this.error = this.translateService.instant(
        'order.internal.form.validation.select_sku_required',
      );
      this.loading = false;
      return;
    }

    if (!this.validateNearestWarehouseForSave()) {
      this.loading = false;
      return;
    }

    try {
      // Recalculate payment amounts right before saving to ensure they're up-to-date
      const allItemsForCalculation = this.tableSections[0].data || [];
      const goodsTotalAmount = allItemsForCalculation.reduce(
        (sum, item) => sum + (item.internalTotal || 0),
        0,
      );
      // Use shippingAmount from form instead of calculating from items
      const shippingAmount = this.formData.shippingAmount || 0;
      const costTotalAmount = allItemsForCalculation.reduce(
        (sum, item) => sum + (item.costPrice || 0),
        0,
      );
      const sellingTotalAmount = allItemsForCalculation.reduce(
        (sum, item) => sum + (item.thirdPartyTotal || 0),
        0,
      );

      const couponCodes = this.getPromotionCouponCodes();
      const selectedOfferIds = [...this.selectedOfferIds];
      const hasPromotionSelection = this.hasPromotionCheckoutSelection(
        couponCodes,
        selectedOfferIds,
      );

      const promotionOrder = this.buildPromotionOrderTableDTO(actualFormValue, {
        forPreview: true,
      });

      let totalDiscount = 0;
      if (hasPromotionSelection) {
        totalDiscount = await this.fetchPromotionPreviewTotalDiscount(
          actualFormValue,
        );
      }
      this.couponDiscountAmount = totalDiscount;
      const paymentAmount =
        sellingTotalAmount + shippingAmount - totalDiscount;

      if (promotionOrder && hasPromotionSelection) {
        promotionOrder.orderPaymentInfoDTO.couponCodes = couponCodes;
        promotionOrder.orderPaymentInfoDTO.selectedOfferIds = selectedOfferIds;
        promotionOrder.orderPaymentInfoDTO.payAmount = paymentAmount;
        promotionOrder.orderPaymentInfoDTO.goodsTotalAmount = goodsTotalAmount;
        promotionOrder.orderPaymentInfoDTO.costTotalAmount = costTotalAmount;
      }

      // Get store info from selectedStore or fallback to finding it in stores array
      const selectedStoreData =
        this.selectedStore ||
        this.o2oOrderService.getSalesStoreById(actualFormValue.salesStore);
      const storeName = selectedStoreData?.label || '';
      // Remove distance info (e.g., " (11.69 km)") from storeName before sending to API
      const cleanStoreName = storeName.replace(
        /\s*\(\d+\.?\d*\s*km\)\s*$/i,
        '',
      );
      const storeCode = selectedStoreData?.spuCode || '';
      const warehouseId = selectedStoreData?.physicalWarehouseId || 0;

      // Determine if delivery method is pickup
      // Use enum values from O2O_TAKING_DELIVERY_WAY (STORE_PICKUP, SHIP_TO_ADDRESS)
      const takingDeliveryWay =
        actualFormValue.takingDeliveryWay || O2O_TAKING_DELIVERY_WAY.pickup;
      const isPickup =
        takingDeliveryWay === O2O_TAKING_DELIVERY_WAY.pickup ||
        takingDeliveryWay === 'STORE_PICKUP' ||
        takingDeliveryWay === this.DELIVERY_METHOD_PICKUP; // Backward compatibility

      // Build buyer info DTO
      // Province/ward are shared between buyer and delivery sections, so include them for both pickup and delivery
      const orderBuyerInfoDTO: any = {
        buyerNick: actualFormValue.buyerNickname || '',
        receiverName: actualFormValue.recipient || '',
        mobile: actualFormValue.recipientPhone || '',
        phone: actualFormValue.recipientPhone || '',
        buyerRemarks: actualFormValue.buyerRemarks || '',
        address: actualFormValue.shippingAddress || '',
        // Include province/ward data for both pickup and delivery (shared fields)
        provinceCode: actualFormValue.provinceId || '',
        province: this.getProvinceName(actualFormValue.provinceId) || '',
        cityCode: '',
        city: actualFormValue.city || '',
        districtCode: '',
        district: actualFormValue.district || '',
        town: actualFormValue.town || '',
        wardCode: actualFormValue.communeWardId || '',
        ward: this.getWardName(actualFormValue.communeWardId) || '',
      };

      // Transform form data to API format - same structure as order but with businessType = 'O2O'
      const orderData: OrderTableDTO = {
        orderBaseInfoDTO: {
          warehouseId,
          channel: actualFormValue.salesChannel || 'website',
          storeId: actualFormValue.salesStore,
          storeCode: storeCode,
          storeName: cleanStoreName,
          outerOrderNo: actualFormValue.thirdPartyOrderNumber || '',
          businessType: 'O2O', // Key difference: O2O instead of RETAIL
          orderTime: actualFormValue.thirdPartyOrderTime,
          payStatus: Number(actualFormValue.paymentStatus) || 0, // Unpaid by default
          orderRemarks: actualFormValue.merchantRemarks || '',
          actionType: 'CREATE',
          copyOrigin: '',
          takingDeliveryStoreId: undefined,
          takingDeliveryStoreName: '',
          salesEmployeeRealName: '',
          takingDeliveryWay:
            actualFormValue.takingDeliveryWay || O2O_TAKING_DELIVERY_WAY.pickup,
          salesEmployeeId: undefined,
        },
        orderBuyerInfoDTO: orderBuyerInfoDTO,
        orderPaymentInfoDTO: {
          payType:
            actualFormValue.paymentStatus === 1
              ? actualFormValue.paymentMethod || null
              : null,
          payAmount: paymentAmount,
          goodsTotalAmount: goodsTotalAmount,
          costTotalAmount: costTotalAmount,
          payTime:
            actualFormValue.paymentStatus === 1
              ? actualFormValue.paymentTime || null
              : null, // Send null if not paid, not empty string
          transactionId:
            actualFormValue.paymentStatus === 1
              ? actualFormValue.transactionId || null
              : null, // Only include transactionId when paid
          shippingAmount: shippingAmount,
          couponCodes,
          selectedOfferIds,
        },
        orderLogisticsInfoDTO: undefined,
        orderItemDTOList: allItems.map((item, index) => {
          const mappedItem = {
            orderItemCode:
              item.orderItemCode || `ITEM-${Date.now()}-${index + 1}`,
            skuId: item.skuId,
            skuCode: item.skuCode,
            skuName: item.skuName,
            quantity: item.quantity,
            outerSaleAmount: item.thirdPartyTotal,
            outerSingleSaleAmount: item.thirdPartyPrice,
            innerSaleAmount: item.internalTotal,
            innerSingleSaleAmount: item.internalPrice,
            outBalancePrice: 0,
            innerBalancePrice: 0,
            setMealFlag: item.setFlag ?? 0,
            giftFlag: item.giftFlag ? 1 : 0,
            mainOrderItemCode: '',
            transPrice: 0,
            systemPickFlag: true,
            activityId: 1,
          };
          return mappedItem;
        }),
      };

      const createdOrder = await firstValueFrom(
        this.orderService.createHandOrder(orderData),
      );

      if (promotionOrder && hasPromotionSelection) {
        // Ensure commit runs after order creation to finalize promotion usage.
        if (
          !promotionOrder.orderBaseInfoDTO.outerOrderNo &&
          createdOrder?.innerOrderNo
        ) {
          promotionOrder.orderBaseInfoDTO.outerOrderNo = createdOrder.innerOrderNo;
        }

        try {
          await firstValueFrom(
            this.promotionCheckout.commit(promotionOrder, 'body', false, {
              httpHeaderAccept: 'application/json' as any,
            }),
          );
        } catch (commitError) {
          console.error('Error committing O2O promotion checkout:', commitError);
          this.messageService.add({
            severity: 'warn',
            summary: this.translateService.instant('common.messages.warning'),
            detail: this.translateService.instant(
              'o2o.create.form.messages.promotion_commit_failed',
            ),
          });
        }
      }

      this.messageService.add({
        severity: 'success',
        summary: this.translateService.instant('common.messages.success'),
        detail: this.translateService.instant(
          'o2o.create.form.messages.order_created_successfully',
        ),
      });

      this.skipCanDeactivate = true;
      this.addFormComponent?.form?.markAsPristine?.();

      try {
        this.removeCoupon();
      } catch (error) {
        console.warn('Error removing coupon after order creation:', error);
      }

      setTimeout(() => {
        try {
          this.resetComponent();
        } catch (error) {
          console.warn('Error resetting component:', error);
        }
        if (createdOrder?.orderId) {
          this.router.navigate(['/o2o/view', createdOrder.orderId]);
        } else {
          this.router.navigate(['/o2o/internal']);
        }
      }, 1500);
    } catch (err: any) {
      console.error('Error creating O2O order:', err);
      // Always show translated Vietnamese message instead of raw error
      this.error = this.translateService.instant(
        'o2o.create.form.messages.failed_to_create_order',
      );

      // Show error toast with translated message
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant(
          'o2o.create.form.messages.failed_to_create_order',
        ),
      });
    } finally {
      this.loading = false;
    }
  }

  private getMissingRequiredFormFieldLabels(
    formValue: Record<string, unknown>,
  ): string[] {
    const missingFieldLabels: string[] = [];
    const checkedControlNames = new Set<string>();

    for (const field of this.formFields) {
      if (!this.shouldValidateRequiredField(field, checkedControlNames)) {
        continue;
      }

      checkedControlNames.add(field.controlName);
      const value = formValue[field.controlName];
      if (this.isRequiredValueMissing(value)) {
        missingFieldLabels.push(this.getTranslatedFieldLabel(field));

        const fieldControl = this.addFormComponent?.form?.get(
          field.controlName,
        );
        if (
          fieldControl &&
          typeof fieldControl.markAsTouched === 'function'
        ) {
          fieldControl.markAsTouched();
        }
      }
    }

    return missingFieldLabels;
  }

  private shouldValidateRequiredField(
    field: FormField,
    checkedControlNames: Set<string>,
  ): boolean {
    return (
      !!field.required &&
      !field.hidden &&
      !field.disabled &&
      !checkedControlNames.has(field.controlName)
    );
  }

  private getTranslatedFieldLabel(field: FormField): string {
    if (field.translateLabel === false) {
      const currentLang = this.translateService.currentLang || 'en';
      if (currentLang.startsWith('vi') && field.labelVi) {
        return field.labelVi;
      }
      return field.label;
    }

    const translatedLabel = this.translateService.instant(field.label);
    return translatedLabel === field.label ? field.label : translatedLabel;
  }

  private isRequiredValueMissing(value: unknown): boolean {
    return (
      value === null ||
      value === undefined ||
      (typeof value === 'string' && value.trim() === '') ||
      (Array.isArray(value) && value.length === 0)
    );
  }

  onCancel(): void {
    // Navigate back to O2O internal list page
    this.router.navigate(['/o2o/internal']);
  }

  canDeactivate(): boolean {
    if (this.skipCanDeactivate || this.loading) {
      return true;
    }
    return !(this.addFormComponent?.form?.dirty ?? false);
  }

  /**
   * Reset component state to initial values (for cancel action)
   */
  private resetComponent(): void {
    this.resetComponentState();
    this.setDefaultValues();
    this.updateSummary();
    this.updateTableActions([]);
    this.updateCouponFieldState();
    this.cdr.detectChanges();
  }

  /**
   * Reset component state without setting defaults (for initialization)
   */
  private resetComponentState(): void {
    // Reset table data and selection
    this.tableSections[0].data = [];
    this.tableSections[0].selection = [];

    // Reset form data
    this.formData = {};

    // Reset nearest warehouse data
    this.nearestWarehouses = [];
    this.nearestSalesStoreOptions = [];
    this.hasAvailableSalesStores = false;

    // Reset already selected SKU codes
    this.alreadySelectedSkuCodes.clear();

    // Reset product dialog state
    this.showProductSelectionDialog = false;
    this._productDialogProducts = [];
    this.productDialogTotalRecords = 0;
    this.productDialogCurrentPage = 1;
    this.productDialogSearchParams = {};

    // Reset error state
    this.error = null;

    // Reset form component if available
    if (this.addFormComponent && this.addFormComponent.form) {
      this.addFormComponent.form.reset();
    }

    // Update coupon field state
    this.updateCouponFieldState();
  }

  addRegularProduct(): void {
    this.addProduct();
  }

  addPackageProduct(): void {
    this.addProduct();
  }

  onDeleteProducts(products: OrderProduct[]): void {
    this.deleteSelectedItems(products);
  }

  onFormValueChanges(value: any): void {
    const previousSelectedOfferIds = [...this.selectedOfferIds];

    // Handle salesStore selection change
    if (value.salesStore !== undefined) {
      this.selectedStore =
        this.o2oOrderService.getSalesStoreById(value.salesStore) ?? undefined;
    }

    // Coupon input is handled via p-autocomplete search/select events
    if (value.selectedPromotionOffers !== undefined) {
      this.selectedOfferIds = Array.isArray(value.selectedPromotionOffers)
        ? value.selectedPromotionOffers
            .map((id: unknown) => Number(id))
            .filter((id: number) => !Number.isNaN(id))
        : [];
    }
    if (value.selectedPromotionOffersDisplay !== undefined) {
      this.selectedPromotionOfferChips = Array.isArray(
        value.selectedPromotionOffersDisplay,
      )
        ? value.selectedPromotionOffersDisplay
            .filter((item: any) => item?.offerId != null)
            .map((item: any) => ({
              offerId: Number(item.offerId),
              offerName: item.offerName || '',
            }))
        : [];
      this.selectedOfferIds = this.selectedPromotionOfferChips.map(
        (item) => item.offerId,
      );
      this.selectedOfferLabels = this.selectedPromotionOfferChips.map(
        (item) => item.offerName,
      );
      Object.assign(this.formData, {
        selectedPromotionOffers: [...this.selectedOfferIds],
      });

      if (
        this.haveSelectedOffersChanged(
          previousSelectedOfferIds,
          this.selectedOfferIds,
        )
      ) {
        this.refreshPromotionPreviewAndSummary();
      }
    }

    // Handle takingDeliveryWay change for conditional validation and reload stores
    if (value.takingDeliveryWay !== undefined) {
      this.updateAddressAndProvinceValidation(value.takingDeliveryWay);
      // Reload stores based on delivery way
      this.reloadSalesStores(value.takingDeliveryWay);
    }

    // Handle provinceId change to load commune/ward options
    if (value.provinceId !== undefined) {
      this.loadCommuneWardOptions(value.provinceId);
      // Try to call nearest warehouse API (will check conditions internally)
      this.tryCallNearestWarehouse();
    }

    // Handle communeWardId change to call nearest warehouse API
    if (value.communeWardId !== undefined) {
      // Try to call nearest warehouse API (will check conditions internally)
      this.tryCallNearestWarehouse();
    }

    // Handle paymentStatus change for conditional field visibility
    if (value.paymentStatus !== undefined) {
      this.updatePaymentFieldsVisibility(value.paymentStatus);
    }

    // Handle order date change - update payment date minDate
    if (value.thirdPartyOrderTime !== undefined) {
      this.updatePaymentDateMinDate(value.thirdPartyOrderTime);
    }

    // Update formData in place to avoid new reference → prevents add-form re-render on every keystroke
    // (fixes Vietnamese IME and number input lag/jitter)
    Object.assign(this.formData, value);

    if (value.additionalValues) {
      const shippingFee = value.additionalValues.includesShippingFee || 0;
      const discountAmount = value.additionalValues.hasThirdPartyDiscount || 0;
      const platformDiscount = value.additionalValues.platformDiscount || 0;
      const merchantDiscount = value.additionalValues.merchantDiscount || 0;

      if (platformDiscount + merchantDiscount > discountAmount) {
        console.warn('Total discount distribution exceeds discount amount');
      }
    }
  }

  private haveSelectedOffersChanged(
    previousOfferIds: number[],
    currentOfferIds: number[],
  ): boolean {
    if (previousOfferIds.length !== currentOfferIds.length) {
      return true;
    }
    const previousSorted = [...previousOfferIds].sort((a, b) => a - b);
    const currentSorted = [...currentOfferIds].sort((a, b) => a - b);
    return previousSorted.some((id, index) => id !== currentSorted[index]);
  }

  /**
   * Update the set of already selected SKU codes based on current table data
   */
  private updateAlreadySelectedSkuCodes(): void {
    this.alreadySelectedSkuCodes.clear();
    this.tableSections[0].data.forEach((item: any) => {
      if (item.skuCode) {
        this.alreadySelectedSkuCodes.add(item.skuCode);
      }
    });
  }

  /**
   * Update address and province validation based on takingDeliveryWay.
   * Shipping address (Số Nhà/Tên Đường) is required only when delivery is SHIP_TO_ADDRESS.
   * Province/City is always visible and selectable (independent of Taking Delivery Way).
   * Province is required only when delivery is selected.
   */
  private updateAddressAndProvinceValidation(takingDeliveryWay: string): void {
    // Compare with both the enum value and string representation for robustness
    const isDelivery =
      takingDeliveryWay === O2O_TAKING_DELIVERY_WAY.delivery ||
      takingDeliveryWay === 'SHIP_TO_ADDRESS';
    const isPickup =
      takingDeliveryWay === O2O_TAKING_DELIVERY_WAY.pickup ||
      takingDeliveryWay === this.DELIVERY_METHOD_PICKUP ||
      takingDeliveryWay === 'STORE_PICKUP';

    // Find ALL delivery information fields by section (more robust than finding individually)
    const deliveryInfoFields = this.formFields.filter(
      (field) =>
        field.section === 'o2o.create.form.sections.delivery_information',
    );

    // Find shippingAddress field in buyer_recipient_information section
    const shippingAddressField = this.formFields.find(
      (field) =>
        field.controlName === 'shippingAddress' &&
        field.section ===
          'o2o.create.form.sections.buyer_recipient_information',
    );

    // Shipping address stays visible; required only for ship-to-address
    if (shippingAddressField) {
      shippingAddressField.hidden = false;
      shippingAddressField.required = isDelivery;
    }

    // Update all delivery information fields visibility and required state
    deliveryInfoFields.forEach((field) => {
      if (!field) return;

      // Set hidden based on delivery method
      field.hidden = !isDelivery;

      // Set required state for specific fields (excluding shippingAddress now)
      if (
        field.controlName === 'provinceId' ||
        field.controlName === 'communeWardId'
      ) {
        field.required = isDelivery;
      }
    });

    // Update province and ward fields in buyer section to have same required state
    // (they share the same FormControl, so validation is shared, but we update required state for UI)
    // Always show as required in buyer section (asterisk always visible)
    const buyerProvinceFields = this.formFields.filter(
      (field) =>
        field.controlName === 'provinceId' &&
        field.section ===
          'o2o.create.form.sections.buyer_recipient_information',
    );
    const buyerWardFields = this.formFields.filter(
      (field) =>
        field.controlName === 'communeWardId' &&
        field.section ===
          'o2o.create.form.sections.buyer_recipient_information',
    );

    buyerProvinceFields.forEach((field) => {
      field.required = true; // Always required in buyer section
    });
    buyerWardFields.forEach((field) => {
      field.required = true; // Always required in buyer section
    });

    // Trigger change detection by reassigning the array reference
    // This ensures Angular detects the changes to the hidden property
    this.formFields = [...this.formFields];

    // Update takingDeliveryWay in the layout component to sync section visibility
    if (this.addFormComponent) {
      this.addFormComponent.updateTakingDeliveryWay(takingDeliveryWay);
    }

    // Also update formData to ensure initialData is in sync
    this.formData.takingDeliveryWay = takingDeliveryWay;

    // Update form validation if form is available
    if (this.addFormComponent && this.addFormComponent.form) {
      const addressControl = this.addFormComponent.form.get('shippingAddress');
      const provinceControl = this.addFormComponent.form.get('provinceId');
      const wardControl = this.addFormComponent.form.get('communeWardId');

      if (addressControl) {
        if (isDelivery) {
          addressControl.setValidators([Validators.required]);
        } else {
          addressControl.clearValidators();
        }
        addressControl.updateValueAndValidity();
      }
      if (wardControl) {
        if (isDelivery) {
          // Add required validator for delivery
          wardControl.setValidators([Validators.required]);
        } else {
          // Clear validators but keep the value - don't clear selected ward
          wardControl.clearValidators();
          // Do not clear the value - preserve user selection
        }
        wardControl.updateValueAndValidity();
      }
      if (provinceControl) {
        if (isDelivery) {
          provinceControl.setValidators([Validators.required]);
        } else {
          provinceControl.clearValidators();
          // Do not clear province value when switching to pickup; keep selection
        }
        provinceControl.updateValueAndValidity();
      }

      // Clear delivery-related fields when switching to pickup
      // Exclude provinceId and communeWardId as they are shared with buyer section and needed for nearest warehouse API
      if (!isDelivery) {
        deliveryInfoFields.forEach((field) => {
          if (field) {
            // Skip province and ward fields - they should be preserved for nearest warehouse API
            if (
              field.controlName === 'provinceId' ||
              field.controlName === 'communeWardId'
            ) {
              return;
            }

            const control = this.addFormComponent?.form.get(field.controlName);
            if (control) {
              // Currency and dimension fields keep 0 so they display "0 VND", "0 cm", "0 g"
              const keepZero =
                field.controlName === 'cod' ||
                field.controlName === 'declaredValue' ||
                field.controlName === 'packageLength' ||
                field.controlName === 'packageWidth' ||
                field.controlName === 'packageHeight' ||
                field.controlName === 'packageWeight';
              control.setValue(keepZero ? 0 : null);
              control.clearValidators();
            }
          }
        });

        // Clear shipping amount and keep currency/dimension fields at 0 when switching to pickup
        this.formData.shippingAmount = 0;
        this.formData.cod = 0;
        this.formData.declaredValue = 0;
        this.formData.packageLength = 0;
        this.formData.packageWidth = 0;
        this.formData.packageHeight = 0;
        this.formData.packageWeight = 0;
        if (this.addFormComponent && this.addFormComponent.form) {
          this.addFormComponent.form.patchValue({
            shippingAmount: 0,
            cod: 0,
            declaredValue: 0,
            packageLength: 0,
            packageWidth: 0,
            packageHeight: 0,
            packageWeight: 0,
          });
        }
        this.updateSummary();
      }
    }

    // Trigger change detection
    this.cdr.detectChanges();
  }

  /**
   * Update payment fields visibility based on payment status
   * If Unpaid (0) -> Hide Payment method, Payment date, Transaction ID
   * If Paid (1) -> Show Payment method, Payment date, Transaction ID
   */
  private updatePaymentFieldsVisibility(paymentStatus: number): void {
    const isPaid = paymentStatus === 1;

    // Find payment-related fields
    const paymentMethodField = this.formFields.find(
      (field) => field.controlName === 'paymentMethod',
    );
    const paymentDateField = this.formFields.find(
      (field) => field.controlName === 'paymentTime',
    );
    const transactionIdField = this.formFields.find(
      (field) => field.controlName === 'transactionId',
    );

    // Update field visibility
    if (paymentMethodField) {
      paymentMethodField.hidden = !isPaid;
      paymentMethodField.required = isPaid;
    }

    if (paymentDateField) {
      paymentDateField.hidden = !isPaid;
    }

    if (transactionIdField) {
      transactionIdField.hidden = !isPaid;
    }

    // Update form validation if form is available
    if (this.addFormComponent && this.addFormComponent.form) {
      const paymentMethodControl =
        this.addFormComponent.form.get('paymentMethod');
      const paymentDateControl = this.addFormComponent.form.get('paymentTime');
      const transactionIdControl =
        this.addFormComponent.form.get('transactionId');

      if (paymentMethodControl) {
        if (isPaid) {
          // Add required validator when paid
          paymentMethodControl.setValidators([Validators.required]);
        } else {
          // Remove validators and clear value when unpaid
          paymentMethodControl.clearValidators();
          paymentMethodControl.setValue(null);
        }
        paymentMethodControl.updateValueAndValidity();
      }

      if (paymentDateControl) {
        if (!isPaid) {
          // Clear value when unpaid
          paymentDateControl.setValue(null);
        }
        paymentDateControl.updateValueAndValidity();
      }

      if (transactionIdControl) {
        if (!isPaid) {
          // Clear value when unpaid
          transactionIdControl.setValue(null);
        }
        transactionIdControl.updateValueAndValidity();
      }
    }

    if (!isPaid) {
      Object.assign(this.formData, {
        paymentMethod: null,
        paymentTime: null,
        transactionId: null,
      });
    }

    // Trigger change detection
    this.cdr.detectChanges();
  }

  /**
   * Custom validator to check if payment date is earlier than order date
   */
  private paymentDateValidator(
    orderDate: Date | null,
  ): (control: AbstractControl) => ValidationErrors | null {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value || !orderDate) {
        return null;
      }

      const paymentDate = new Date(control.value);
      paymentDate.setHours(0, 0, 0, 0);
      const orderDateOnly = new Date(orderDate);
      orderDateOnly.setHours(0, 0, 0, 0);

      if (paymentDate < orderDateOnly) {
        return { paymentDateEarlierThanOrderDate: true };
      }

      return null;
    };
  }

  /**
   * Update payment date minDate based on selected order date
   * Payment date cannot be earlier than order date
   */
  private updatePaymentDateMinDate(orderDate: Date | null): void {
    const paymentDateField = this.formFields.find(
      (field) => field.controlName === 'paymentTime',
    );

    if (!paymentDateField) {
      return;
    }

    if (orderDate) {
      // Set minDate to the selected order date
      const minDate = new Date(orderDate);
      minDate.setHours(0, 0, 0, 0); // Set to start of day
      paymentDateField.minDate = minDate;

      // Add error message for custom validator
      if (!paymentDateField.errorMessages) {
        paymentDateField.errorMessages = {};
      }
      paymentDateField.errorMessages['paymentDateEarlierThanOrderDate'] =
        'o2o.create.form.validation.payment_date_earlier_than_order_date';

      // Update validator for payment date control
      if (this.addFormComponent && this.addFormComponent.form) {
        const paymentDateControl =
          this.addFormComponent.form.get('paymentTime');
        if (paymentDateControl) {
          // Get existing validators by checking the field configuration
          const paymentDateField = this.formFields.find(
            (field) => field.controlName === 'paymentTime',
          );
          const existingValidators: any[] = [];

          // Preserve required validator if field is required
          if (paymentDateField?.required) {
            existingValidators.push(Validators.required);
          }

          // Preserve other custom validators from field.validators
          if (paymentDateField?.validators) {
            existingValidators.push(...paymentDateField.validators);
          }

          // Add custom validator for date comparison
          const customValidator = this.paymentDateValidator(orderDate);
          paymentDateControl.setValidators([
            ...existingValidators,
            customValidator,
          ]);
          paymentDateControl.updateValueAndValidity();

          // Mark as touched to show error if invalid
          if (paymentDateControl.invalid) {
            paymentDateControl.markAsTouched();
          }
        }
      }
    } else {
      // If no order date selected, reset to default minDate
      paymentDateField.minDate = new Date(2025, 1, 1);

      // Remove error message
      if (paymentDateField.errorMessages) {
        delete paymentDateField.errorMessages[
          'paymentDateEarlierThanOrderDate'
        ];
      }

      // Remove custom validator
      if (this.addFormComponent && this.addFormComponent.form) {
        const paymentDateControl =
          this.addFormComponent.form.get('paymentTime');
        if (paymentDateControl) {
          // Get existing validators from field configuration
          const paymentDateField = this.formFields.find(
            (field) => field.controlName === 'paymentTime',
          );
          const existingValidators: any[] = [];

          // Preserve required validator if field is required
          if (paymentDateField?.required) {
            existingValidators.push(Validators.required);
          }

          // Preserve other custom validators from field.validators
          if (paymentDateField?.validators) {
            existingValidators.push(...paymentDateField.validators);
          }

          paymentDateControl.setValidators(
            existingValidators.length > 0 ? existingValidators : null,
          );
          paymentDateControl.updateValueAndValidity();
        }
      }
    }

    // Trigger change detection
    this.cdr.detectChanges();
  }

  /**
   * Update coupon field state based on whether table has items
   */
  private updateCouponFieldState(): void {
    const hasItems =
      this.tableSections[0].data && this.tableSections[0].data.length > 0;
    const shouldDisable = !hasItems;

    const couponField = this.formFields.find(
      (field) => field.controlName === 'couponDiscount',
    );
    const offersField = this.formFields.find(
      (field) => field.controlName === 'selectedPromotionOffers',
    );

    if (couponField) {
      couponField.disabled = shouldDisable;
      couponField.readonly = shouldDisable;

      if (this.addFormComponent?.form) {
        const couponControl = this.addFormComponent.form.get('couponDiscount');
        if (couponControl) {
          if (shouldDisable) {
            couponControl.disable({ emitEvent: false });
          } else {
            couponControl.enable({ emitEvent: false });
          }
        }
      }

      if (
        !hasItems &&
        (this.appliedCoupons.length > 0 || this.selectedCoupon)
      ) {
        this.selectedCoupon = null;
        this.appliedCoupons = [];
        this.couponDiscountAmount = 0;
        this.couponInputError = null;
        this.lastValidatedCode = null;
        this.couponSuggestions = {};
        this.addFormComponent?.clearCouponSuggestions('couponDiscount');

        if (this.couponValidationTimeout) {
          clearTimeout(this.couponValidationTimeout);
          this.couponValidationTimeout = null;
        }

        if (this.addFormComponent?.form) {
          this.addFormComponent.form.patchValue({ couponDiscount: '' });
        }
        Object.assign(this.formData, { couponDiscount: '' });
      }
    }

    if (offersField) {
      const canLoadOffers =
        !shouldDisable && this.hasPromotionCheckoutContext();
      if (!canLoadOffers) {
        this.clearAvailablePromotions();
      } else if (!(offersField.options?.length)) {
        offersField.disabled = true;
        this.addFormComponent?.form
          ?.get('selectedPromotionOffers')
          ?.disable({ emitEvent: false });
      }
    }

    if (!hasItems) {
      this.couponDiscountAmount = 0;
      this.clearAvailablePromotions();
    }
  }
}
